import React, { useState, useEffect } from 'react';
import { ref, onValue, runTransaction, update, get, set } from 'firebase/database';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { db, storage } from '../firebase/config';
import { FaSearch, FaTimes, FaShoppingCart, FaPlus, FaMinus, FaTrash } from 'react-icons/fa';
import '../styles/ProductCard.css';
import panchgiriLogo from '../img/Panchgiri Logo.jpg';
import { calculateGst, formatGstForInvoice } from '../utils/gstSettings';



const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [cart, setCart] = useState([]);
  const [showCartModal, setShowCartModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [walletBalance, setWalletBalance] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [isNewCustomer, setIsNewCustomer] = useState(true);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [buyNowProduct, setBuyNowProduct] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [loadingMessage, setLoadingMessage] = useState('Processing your order...');


  // ADD THESE HELPER FUNCTIONS HERE (after initialFormData)

  // Format date for display: "2003-10-06" → "06 Oct 2003"
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return 'N/A';

    // Handle DD/MM/YYYY format
    if (dateString.includes('/')) {
      const [day, month, year] = dateString.split('/');
      const date = new Date(`${year}-${month}-${day}`);

      const dayNum = String(date.getDate()).padStart(2, '0');
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const monthName = months[date.getMonth()];
      const yearNum = date.getFullYear();

      return `${dayNum} ${monthName} ${yearNum}`;
    }

    // Handle ISO format (YYYY-MM-DD)
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    return `${day} ${month} ${year}`;
  };

  // Convert DD/MM/YYYY to YYYY-MM-DD for storage
  const convertDDMMYYYYtoISO = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';

    const parts = ddmmyyyy.split('/');
    if (parts.length !== 3) return ddmmyyyy;

    const [day, month, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  };

  // Convert YYYY-MM-DD to DD/MM/YYYY for input field
  const convertISOtoDDMMYYYY = (isoDate) => {
    if (!isoDate) return '';

    const parts = isoDate.split('-');
    if (parts.length !== 3) return isoDate;

    const [year, month, day] = parts;
    return `${day}/${month}/${year}`;
  };

  const handleBirthDateInput = (e) => {
    let value = e.target.value.replace(/[^0-9]/g, ''); // Only numbers

    // Format as DD/MM/YYYY
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + '/' + value.slice(5, 9);
    }

    if (value.length <= 10) {
      const error = validateField('birthDate', value);
      setFormData(prev => ({ ...prev, birthDate: value }));
      setValidationErrors(prev => ({ ...prev, birthDate: error }));
    }
  };

  // Indian States and Districts Data
  const statesAndDistricts = {
    "Andhra Pradesh": ["Anantapur", "Chittoor", "East Godavari", "Guntur", "Krishna", "Kurnool", "Prakasam", "Srikakulam", "Visakhapatnam", "Vizianagaram", "West Godavari", "YSR Kadapa"],
    "Arunachal Pradesh": ["Anjaw", "Changlang", "Dibang Valley", "East Kameng", "East Siang", "Kamle", "Kra Daadi", "Kurung Kumey", "Lepa Rada", "Lohit", "Longding", "Lower Dibang Valley", "Lower Siang", "Lower Subansiri", "Namsai", "Pakke Kessang", "Papum Pare", "Shi Yomi", "Siang", "Tawang", "Tirap", "Upper Siang", "Upper Subansiri", "West Kameng", "West Siang"],
    "Assam": ["Baksa", "Barpeta", "Biswanath", "Bongaigaon", "Cachar", "Charaideo", "Chirang", "Darrang", "Dhemaji", "Dhubri", "Dibrugarh", "Goalpara", "Golaghat", "Hailakandi", "Hojai", "Jorhat", "Kamrup", "Kamrup Metropolitan", "Karbi Anglong", "Karimganj", "Kokrajhar", "Lakhimpur", "Majuli", "Morigaon", "Nagaon", "Nalbari", "Dima Hasao", "Sivasagar", "Sonitpur", "South Salmara-Mankachar", "Tinsukia", "Udalguri", "West Karbi Anglong"],
    "Bihar": ["Araria", "Arwal", "Aurangabad", "Banka", "Begusarai", "Bhagalpur", "Bhojpur", "Buxar", "Darbhanga", "East Champaran", "Gaya", "Gopalganj", "Jamui", "Jehanabad", "Kaimur", "Katihar", "Khagaria", "Kishanganj", "Lakhisarai", "Madhepura", "Madhubani", "Munger", "Muzaffarpur", "Nalanda", "Nawada", "Patna", "Purnia", "Rohtas", "Saharsa", "Samastipur", "Saran", "Sheikhpura", "Sheohar", "Sitamarhi", "Siwan", "Supaul", "Vaishali", "West Champaran"],
    "Chhattisgarh": ["Balod", "Baloda Bazar", "Balrampur", "Bastar", "Bemetara", "Bijapur", "Bilaspur", "Dantewada", "Dhamtari", "Durg", "Gariaband", "Janjgir Champa", "Jashpur", "Kabirdham", "Kanker", "Kondagaon", "Korba", "Koriya", "Mahasamund", "Mungeli", "Narayanpur", "Raigarh", "Raipur", "Rajnandgaon", "Sukma", "Surajpur", "Surguja"],
    "Goa": ["North Goa", "South Goa"],
    "Gujarat": ["Ahmedabad", "Amreli", "Anand", "Aravalli", "Banaskantha", "Bharuch", "Bhavnagar", "Botad", "Chhota Udaipur", "Dahod", "Dang", "Devbhoomi Dwarka", "Gandhinagar", "Gir Somnath", "Jamnagar", "Junagadh", "Kheda", "Kutch", "Mahisagar", "Mehsana", "Morbi", "Narmada", "Navsari", "Panchmahal", "Patan", "Porbandar", "Rajkot", "Sabarkantha", "Surat", "Surendranagar", "Tapi", "Vadodara", "Valsad"],
    "Haryana": ["Ambala", "Bhiwani", "Charkhi Dadri", "Faridabad", "Fatehabad", "Gurugram", "Hisar", "Jhajjar", "Jind", "Kaithal", "Karnal", "Kurukshetra", "Mahendragarh", "Nuh", "Palwal", "Panchkula", "Panipat", "Rewari", "Rohtak", "Sirsa", "Sonipat", "Yamunanagar"],
    "Himachal Pradesh": ["Bilaspur", "Chamba", "Hamirpur", "Kangra", "Kinnaur", "Kullu", "Lahaul and Spiti", "Mandi", "Shimla", "Sirmaur", "Solan", "Una"],
    "Jharkhand": ["Bokaro", "Chatra", "Deoghar", "Dhanbad", "Dumka", "East Singhbhum", "Garhwa", "Giridih", "Godda", "Gumla", "Hazaribagh", "Jamtara", "Khunti", "Koderma", "Latehar", "Lohardaga", "Pakur", "Palamu", "Ramgarh", "Ranchi", "Sahebganj", "Seraikela Kharsawan", "Simdega", "West Singhbhum"],
    "Karnataka": ["Bagalkot", "Ballari", "Belagavi", "Bengaluru Rural", "Bengaluru Urban", "Bidar", "Chamarajanagar", "Chikballapur", "Chikkamagaluru", "Chitradurga", "Dakshina Kannada", "Davanagere", "Dharwad", "Gadag", "Hassan", "Haveri", "Kalaburagi", "Kodagu", "Kolar", "Koppal", "Mandya", "Mysuru", "Raichur", "Ramanagara", "Shivamogga", "Tumakuru", "Udupi", "Uttara Kannada", "Vijayapura", "Yadgir"],
    "Kerala": ["Alappuzha", "Ernakulam", "Idukki", "Kannur", "Kasaragod", "Kollam", "Kottayam", "Kozhikode", "Malappuram", "Palakkad", "Pathanamthitta", "Thiruvananthapuram", "Thrissur", "Wayanad"],
    "Madhya Pradesh": ["Agar Malwa", "Alirajpur", "Anuppur", "Ashoknagar", "Balaghat", "Barwani", "Betul", "Bhind", "Bhopal", "Burhanpur", "Chhatarpur", "Chhindwara", "Damoh", "Datia", "Dewas", "Dhar", "Dindori", "Guna", "Gwalior", "Harda", "Hoshangabad", "Indore", "Jabalpur", "Jhabua", "Katni", "Khandwa", "Khargone", "Mandla", "Mandsaur", "Morena", "Narsinghpur", "Neemuch", "Niwari", "Panna", "Raisen", "Rajgarh", "Ratlam", "Rewa", "Sagar", "Satna", "Sehore", "Seoni", "Shahdol", "Shajapur", "Sheopur", "Shivpuri", "Sidhi", "Singrauli", "Tikamgarh", "Ujjain", "Umaria", "Vidisha"],
    "Maharashtra": [
      "Ahilyanagar",
      "Akola",
      "Amravati",
      "Beed",
      "Bhandara",
      "Buldhana",
      "Chandrapur",
      "Chhatrapti Sambhajinagar",
      "Dhule",
      "Dharashiv",
      "Gadchiroli",
      "Gondia",
      "Hingoli",
      "Jalgaon",
      "Jalna",
      "Kolhapur",
      "Latur",
      "Mumbai City",
      "Mumbai Suburban",
      "Nagpur",
      "Nanded",
      "Nandurbar",
      "Nashik",
      "Palghar",
      "Parbhani",
      "Pune",
      "Raigad",
      "Ratnagiri",
      "Sangli",
      "Satara",
      "Sindhudurg",
      "Solapur",
      "Thane",
      "Wardha",
      "Washim",
      "Yavatmal"
    ],

    "Manipur": ["Bishnupur", "Chandel", "Churachandpur", "Imphal East", "Imphal West", "Jiribam", "Kakching", "Kamjong", "Kangpokpi", "Noney", "Pherzawl", "Senapati", "Tamenglong", "Tengnoupal", "Thoubal", "Ukhrul"],
    "Meghalaya": ["East Garo Hills", "East Jaintia Hills", "East Khasi Hills", "North Garo Hills", "Ri Bhoi", "South Garo Hills", "South West Garo Hills", "South West Khasi Hills", "West Garo Hills", "West Jaintia Hills", "West Khasi Hills"],
    "Mizoram": ["Aizawl", "Champhai", "Hnahthial", "Kolasib", "Khawzawl", "Lawngtlai", "Lunglei", "Mamit", "Saiha", "Saitual", "Serchhip"],
    "Nagaland": ["Dimapur", "Kiphire", "Kohima", "Longleng", "Mokokchung", "Mon", "Peren", "Phek", "Tuensang", "Wokha", "Zunheboto"],
    "Odisha": ["Angul", "Balangir", "Balasore", "Bargarh", "Bhadrak", "Boudh", "Cuttack", "Deogarh", "Dhenkanal", "Gajapati", "Ganjam", "Jagatsinghpur", "Jajpur", "Jharsuguda", "Kalahandi", "Kandhamal", "Kendrapara", "Kendujhar", "Khordha", "Koraput", "Malkangiri", "Mayurbhanj", "Nabarangpur", "Nayagarh", "Nuapada", "Puri", "Rayagada", "Sambalpur", "Subarnapur", "Sundargarh"],
    "Punjab": ["Amritsar", "Barnala", "Bathinda", "Faridkot", "Fatehgarh Sahib", "Fazilka", "Ferozepur", "Gurdaspur", "Hoshiarpur", "Jalandhar", "Kapurthala", "Ludhiana", "Mansa", "Moga", "Muktsar", "Nawanshahr", "Pathankot", "Patiala", "Rupnagar", "Sangrur", "Tarn Taran"],
    "Rajasthan": ["Ajmer", "Alwar", "Banswara", "Baran", "Barmer", "Bharatpur", "Bhilwara", "Bikaner", "Bundi", "Chittorgarh", "Churu", "Dausa", "Dholpur", "Dungarpur", "Hanumangarh", "Jaipur", "Jaisalmer", "Jalore", "Jhalawar", "Jhunjhunu", "Jodhpur", "Karauli", "Kota", "Nagaur", "Pali", "Pratapgarh", "Rajsamand", "Sawai Madhopur", "Sikar", "Sirohi", "Sri Ganganagar", "Tonk", "Udaipur"],
    "Sikkim": ["East Sikkim", "North Sikkim", "South Sikkim", "West Sikkim"],
    "Tamil Nadu": ["Ariyalur", "Chengalpattu", "Chennai", "Coimbatore", "Cuddalore", "Dharmapuri", "Dindigul", "Erode", "Kallakurichi", "Kanchipuram", "Kanyakumari", "Karur", "Krishnagiri", "Madurai", "Mayiladuthurai", "Nagapattinam", "Namakkal", "Nilgiris", "Perambalur", "Pudukkottai", "Ramanathapuram", "Ranipet", "Salem", "Sivaganga", "Tenkasi", "Thanjavur", "Theni", "Thoothukudi", "Tiruchirappalli", "Tirunelveli", "Tirupathur", "Tiruppur", "Tiruvallur", "Tiruvannamalai", "Tiruvarur", "Vellore", "Viluppuram", "Virudhunagar"],
    "Telangana": ["Adilabad", "Bhadradri Kothagudem", "Hyderabad", "Jagtial", "Jangaon", "Jayashankar", "Jogulamba", "Kamareddy", "Karimnagar", "Khammam", "Komaram Bheem", "Mahabubabad", "Mahbubnagar", "Mancherial", "Medak", "Medchal", "Mulugu", "Nagarkurnool", "Nalgonda", "Narayanpet", "Nirmal", "Nizamabad", "Peddapalli", "Rajanna Sircilla", "Ranga Reddy", "Sangareddy", "Siddipet", "Suryapet", "Vikarabad", "Wanaparthy", "Warangal Rural", "Warangal Urban", "Yadadri Bhuvanagiri"],
    "Tripura": ["Dhalai", "Gomati", "Khowai", "North Tripura", "Sepahijala", "South Tripura", "Unakoti", "West Tripura"],
    "Uttar Pradesh": ["Agra", "Aligarh", "Ambedkar Nagar", "Amethi", "Amroha", "Auraiya", "Ayodhya", "Azamgarh", "Baghpat", "Bahraich", "Ballia", "Balrampur", "Banda", "Barabanki", "Bareilly", "Basti", "Bhadohi", "Bijnor", "Budaun", "Bulandshahr", "Chandauli", "Chitrakoot", "Deoria", "Etah", "Etawah", "Farrukhabad", "Fatehpur", "Firozabad", "Gautam Buddha Nagar", "Ghaziabad", "Ghazipur", "Gonda", "Gorakhpur", "Hamirpur", "Hapur", "Hardoi", "Hathras", "Jalaun", "Jaunpur", "Jhansi", "Kannauj", "Kanpur Dehat", "Kanpur Nagar", "Kasganj", "Kaushambi", "Kheri", "Kushinagar", "Lalitpur", "Lucknow", "Maharajganj", "Mahoba", "Mainpuri", "Mathura", "Mau", "Meerut", "Mirzapur", "Moradabad", "Muzaffarnagar", "Pilibhit", "Pratapgarh", "Prayagraj", "Raebareli", "Rampur", "Saharanpur", "Sambhal", "Sant Kabir Nagar", "Shahjahanpur", "Shamli", "Shrawasti", "Siddharthnagar", "Sitapur", "Sonbhadra", "Sultanpur", "Unnao", "Varanasi"],
    "Uttarakhand": ["Almora", "Bageshwar", "Chamoli", "Champawat", "Dehradun", "Haridwar", "Nainital", "Pauri", "Pithoragarh", "Rudraprayag", "Tehri", "Udham Singh Nagar", "Uttarkashi"],
    "West Bengal": ["Alipurduar", "Bankura", "Birbhum", "Cooch Behar", "Dakshin Dinajpur", "Darjeeling", "Hooghly", "Howrah", "Jalpaiguri", "Jhargram", "Kalimpong", "Kolkata", "Malda", "Murshidabad", "Nadia", "North 24 Parganas", "Paschim Bardhaman", "Paschim Medinipur", "Purba Bardhaman", "Purba Medinipur", "Purulia", "South 24 Parganas", "Uttar Dinajpur"]
  };

  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    address: '',
    landmark: '',
    city: '',
    state: '',
    postalCode: '',
    birthDate: '',
    paymentMethod: 'Online', // Default to online payment
  };
  const [formData, setFormData] = useState(initialFormData);

  const user = JSON.parse(localStorage.getItem('htamsUser'));

  // No need to load Razorpay SDK for payment links


  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  const validatePostalCode = (postalCode) => {
    const postalRegex = /^[1-9][0-9]{5}$/;
    return postalRegex.test(postalCode);
  };

  const validateName = (name) => {
    const nameRegex = /^[a-zA-Z\s]{2,50}$/;
    return nameRegex.test(name.trim());
  };

  const validateCityState = (value) => {
    const cityStateRegex = /^[a-zA-Z\s]{2,30}$/;
    return cityStateRegex.test(value.trim());
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'name':
        if (!value.trim()) {
          error = 'Name is required';
        } else if (!validateName(value)) {
          error = 'Name should contain only letters and spaces (2-50 characters)';
        }
        break;

      case 'email':
        if (!value.trim()) {
          error = 'Email is required';
        } else if (!validateEmail(value)) {
          error = 'Please enter a valid email address';
        }
        break;

      case 'phone':
        if (!value.trim()) {
          error = 'Phone number is required';
        } else if (!validatePhone(value)) {
          error = 'Please enter a valid 10-digit mobile number starting with 6-9';
        }
        break;

      case 'address':
        if (!value.trim()) {
          error = 'Address is required';
        } else if (value.trim().length < 10) {
          error = 'Address should be at least 10 characters long';
        }
        break;

      case 'city':
        if (!value.trim()) {
          error = 'City is required';
        } else if (!validateCityState(value)) {
          error = 'City should contain only letters and spaces (2-30 characters)';
        }
        break;

      case 'state':
        if (!value.trim()) {
          error = 'State is required';
        } else if (!validateCityState(value)) {
          error = 'State should contain only letters and spaces (2-30 characters)';
        }
        break;

      case 'postalCode':
        if (!value.trim()) {
          error = 'Postal code is required';
        } else if (!validatePostalCode(value)) {
          error = 'Please enter a valid 6-digit postal code';
        }
        break;

      case 'birthDate':
        if (!value.trim()) {
          error = 'Birth date is required';
        } else {
          // Convert DD/MM/YYYY to Date object for validation
          let birthDate;
          if (value.includes('/')) {
            const [day, month, year] = value.split('/');
            birthDate = new Date(`${year}-${month}-${day}`);
          } else {
            birthDate = new Date(value);
          }

          const today = new Date();
          const age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          if (isNaN(birthDate.getTime())) {
            error = 'Please enter a valid date in DD/MM/YYYY format';
          } else if (birthDate > today) {
            error = 'Birth date cannot be in the future';
          } else if (age < 13 || (age === 13 && monthDiff < 0)) {
            error = 'You must be at least 13 years old';
          } else if (age > 120) {
            error = 'Please enter a valid birth date';
          }
        }
        break;


      default:
        break;
    }

    return error;
  };

  const validateAllFields = () => {
    const errors = {};
    const requiredFields = ['name', 'email', 'phone', 'address', 'city', 'state', 'postalCode', 'birthDate'];

    requiredFields.forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        errors[field] = error;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (user?.uid) {
      const walletRef = ref(db, `HTAMS/users/${user.uid}/MySales`);
      onValue(walletRef, (snapshot) => {
        const balance = Number(snapshot.val()) || 0;
        setWalletBalance(balance);
        console.log('Wallet balance fetched:', balance);
      });
    }
  }, [user?.uid]);

  // Auto-select payment method based on wallet balance and cart total
  useEffect(() => {
    const cartTotal = getCartTotal();
    if (cartTotal > 0) {
      if (cartTotal <= walletBalance && walletBalance > 0) {
        // If wallet has sufficient balance, prefer wallet payment
        setFormData(prev => ({ ...prev, paymentMethod: 'Wallet' }));
      } else {
        // If wallet is insufficient or empty, default to online payment
        setFormData(prev => ({ ...prev, paymentMethod: 'Online' }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletBalance, cart]);

  const handleBuyNow = (product) => {
    // Add product to existing cart (don't clear it!)
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        // If product already in cart, just increase quantity by 1
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // If new product, add with quantity 1 to existing cart
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });

    // Immediately open the cart modal
    setShowCartModal(true);

    // Show notification
    showNotification(
      `Added to cart and ready to checkout`,
      `🛒 ${product.name}`,
      'success'
    );
  };



  useEffect(() => {
    const productsRef = ref(db, 'HTAMS/company/products');
    const unsubscribeProducts = onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      const productsArray = data ? Object.entries(data).map(([id, val], index) => ({
        id,
        serialNo: index + 1,
        ...val,
        stock: Number(val.stock) || 0,
        price: Number(val.price) || 0,
        imageUrl: val.imageUrls && val.imageUrls.length > 0 ? val.imageUrls[0] : 'https://via.placeholder.com/300'
      })) : [];
      setProducts(productsArray);
    });

    return () => unsubscribeProducts();
  }, []);

  // Fetch customers for selection
  useEffect(() => {
    const customersRef = ref(db, 'HTAMS/customers');
    const unsubscribeCustomers = onValue(customersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const customersArray = Object.entries(data).map(([phone, customerData]) => ({
          phone,
          ...customerData,
          displayName: `${customerData.name} (${phone})`
        }));
        setCustomers(customersArray);
      } else {
        setCustomers([]);
      }
    });

    return () => unsubscribeCustomers();
  }, []);

  // Collect phones of customers whose orders were placed by the current user
  useEffect(() => {
    if (!user?.uid) return;
    const ordersRef = ref(db, 'HTAMS/orders');
    const unsubscribe = onValue(ordersRef, (snapshot) => {
      const data = snapshot.val() || {};
      const mine = new Set();
      Object.values(data).forEach((o) => {
        if (o?.placedBy === user.uid && o?.customerPhone) {
          mine.add(o.customerPhone);
        }
      });
      setMyCustomerPhones(mine);
    });
    return () => unsubscribe();
  }, [user?.uid]);

  useEffect(() => {
    const lowercasedQuery = searchQuery.toLowerCase();
    const filtered = products.filter(prod => {
      const hasStock = prod.stock > 0;
      const nameMatch = prod.name.toLowerCase().includes(lowercasedQuery);
      const priceMatch = prod.price.toString().includes(lowercasedQuery);
      const serialNoMatch = prod.serialNo.toString().includes(lowercasedQuery);
      const searchMatch = nameMatch || priceMatch || serialNoMatch;
      return hasStock && searchMatch;
    });
    setFilteredProducts(filtered);
  }, [searchQuery, products]);



  const [notifications, setNotifications] = useState([]);
  const [availableDistricts, setAvailableDistricts] = useState([]);
  const [myCustomerPhones, setMyCustomerPhones] = useState(new Set());

  const showNotification = (message, title, type = 'success') => {
    const id = `${Date.now()}_${Math.random()}`;
    const newNotification = { id, message, title, type };

    setNotifications(prev => {
      // Check if exact same notification already exists
      const isDuplicate = prev.some(n =>
        n.title === title &&
        n.message === message &&
        n.type === type
      );

      if (isDuplicate) {
        return prev; // Don't add duplicate
      }

      return [...prev, newNotification];
    });

    // Auto hide notification after 3 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 1000);
  };

  const addToCart = (product) => {
    const productCard = document.querySelector(`[data-product-id="${product.id}"]`);
    if (productCard) {
      productCard.classList.add('added-to-cart');
      setTimeout(() => productCard.classList.remove('added-to-cart'), 300);
    }

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        // Item already exists - don't show notification here, 
        // let updateCartQuantity handle it
        return prevCart;
      } else {
        // New item - show add notification
        showNotification(
          `Successfully added to  cart`,
          `🎉 ${product.name}`,
          'success'
        );
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });
  };

  const handleBuyNowClick = (product) => {
    // Add product to cart (quantity 1)
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.id === product.id);
      if (existingItem) {
        // If product already in cart, just increase quantity by 1
        return prevCart.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // If new product, add with quantity 1
        return [...prevCart, { ...product, quantity: 1 }];
      }
    });

    // Immediately open the cart modal
    setShowCartModal(true);

    // Show notification
    showNotification(
      `Added to cart and opened checkout`,
      `🛒 ${product.name}`,
      'success'
    );
  };


  const removeFromCart = (productId) => {
    const product = cart.find(item => item.id === productId);
    if (product) {
      setCart(prevCart => prevCart.filter(item => item.id !== productId));
      showNotification(
        `Removed from your cart`,
        `🗑️ ${product.name}`,
        'error'
      );
    }
  };

  const updateCartQuantity = (productId, newQuantity) => {
    const currentItem = cart.find(item => item.id === productId);
    const currentQuantity = currentItem?.quantity || 0;

    if (newQuantity <= 0) {
      const product = cart.find(item => item.id === productId);
      if (product) {
        showNotification(
          `Removed from your cart`,
          `🗑️ ${product.name}`,
          'error'
        );
      }
      setCart(prevCart => prevCart.filter(item => item.id !== productId));
      return;
    }

    setCart(prevCart =>
      prevCart.map(item => {
        if (item.id === productId) {
          const maxQuantity = products.find(p => p.id === productId)?.stock || 0;
          if (newQuantity > maxQuantity) {
            showNotification(
              `Only ${maxQuantity} items available`,
              `⚠️ ${item.name}`,
              'warning'
            );
            return item;
          }

          // Show different notification based on increase or decrease
          if (newQuantity > currentQuantity) {
            showNotification(
              `Quantity increased to ${newQuantity}`,
              `➕ ${item.name}`,
              'success'
            );
          } else {
            showNotification(
              `Quantity decreased to ${newQuantity}`,
              `➖ ${item.name}`,
              'info'
            );
          }

          return { ...item, quantity: newQuantity };
        }
        return item;
      }).filter(item => item.quantity > 0)
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.mrp * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const clearCart = () => {
    const itemCount = cart.length;
    setCart([]);
    showNotification(
      `${itemCount} ${itemCount === 1 ? 'item' : 'items'} removed`,
      '🧹 Cart Cleared',
      'info'
    );
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => ({ ...prev, [name]: value }));

    const error = validateField(name, value);
    setValidationErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };

  const handleStateChange = (e) => {
    const selectedState = e.target.value;
    setFormData(prev => ({
      ...prev,
      state: selectedState,
      city: '' // Reset city when state changes
    }));

    // Update available districts based on selected state
    if (selectedState && statesAndDistricts[selectedState]) {
      setAvailableDistricts(statesAndDistricts[selectedState]);
    } else {
      setAvailableDistricts([]);
    }

    const error = validateField('state', selectedState);
    setValidationErrors(prev => ({
      ...prev,
      state: error,
      city: '' // Reset city validation
    }));
  };

  // Handle customer selection
  const handleCustomerSelection = (e) => {
    const customerPhone = e.target.value;
    setSelectedCustomer(customerPhone);

    if (customerPhone === 'new') {
      // New customer selected
      setIsNewCustomer(true);
      setFormData(initialFormData);
      setValidationErrors({});
      setAvailableDistricts([]);
    } else if (customerPhone) {
      // Existing customer selected
      setIsNewCustomer(false);
      const customer = customers.find(c => c.phone === customerPhone);
      if (customer) {
        // Auto-fill form with customer data
        const customerAddress = customer.address || {};
        setFormData({
          name: customer.name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          address: customerAddress.street || '',
          landmark: customerAddress.landmark || '',
          city: customerAddress.city || '',
          state: customerAddress.state || '',
          postalCode: customerAddress.postalCode || '',
          birthDate: convertISOtoDDMMYYYY(customer.birthDate) || '',

          paymentMethod: 'Online'
        });

        // Set available districts if state is selected
        if (customerAddress.state && statesAndDistricts[customerAddress.state]) {
          setAvailableDistricts(statesAndDistricts[customerAddress.state]);
        }

        // Clear validation errors
        setValidationErrors({});

        // Fetch customer's previous orders
        fetchCustomerOrders(customer.phone);

        showNotification(
          `Customer information loaded successfully`,
          `👤 ${customer.name}`,
          'success'
        );
      }
    } else {
      // No selection
      setIsNewCustomer(true);
      setFormData(initialFormData);
      setValidationErrors({});
      setAvailableDistricts([]);
    }
  };

  // Fetch customer's previous orders
  const fetchCustomerOrders = async (customerPhone) => {
    try {
      const ordersRef = ref(db, 'HTAMS/orders');
      const ordersSnapshot = await get(ordersRef);

      if (ordersSnapshot.exists()) {
        const allOrders = ordersSnapshot.val();
        const customerOrdersList = Object.entries(allOrders)
          .filter(([orderId, order]) => order.customerPhone === customerPhone)
          .map(([orderId, order]) => ({
            orderId,
            ...order,
            orderDate: new Date(order.orderDate).toLocaleDateString('en-IN'),
            totalAmount: Number(order.totalAmount) || 0
          }))
          .sort((a, b) => new Date(b.orderDate) - new Date(a.orderDate))
          .slice(0, 5); // Show only last 5 orders

        setCustomerOrders(customerOrdersList);
      } else {
        setCustomerOrders([]);
      }
    } catch (error) {
      console.error('Error fetching customer orders:', error);
      setCustomerOrders([]);
    }
  };

  const handlePhoneInput = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 10) {
      const error = validateField('phone', value);
      setFormData(prev => ({ ...prev, phone: value }));
      setValidationErrors(prev => ({
        ...prev,
        phone: error
      }));
    }
  };

  const handlePostalCodeInput = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 6) {
      const error = validateField('postalCode', value);
      setFormData(prev => ({ ...prev, postalCode: value }));
      setValidationErrors(prev => ({
        ...prev,
        postalCode: error
      }));
    }
  };

  const handleNameInput = (e) => {
    const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
    const error = validateField(e.target.name, value);
    setFormData(prev => ({ ...prev, [e.target.name]: value }));
    setValidationErrors(prev => ({
      ...prev,
      [e.target.name]: error
    }));
  };



  const generateOrderId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `order_${timestamp}_${random}`;
  };

  // Save customer data for admin tracking
  const saveCustomerData = async (customerData, reason = 'form_filled') => {
    try {
      const customerKey = `${customerData.phone}_${customerData.email}`;
      const customerRef = ref(db, `HTAMS/customer_leads/${customerKey}`);

      // Check if customer data already exists to prevent duplicates
      const existingSnapshot = await get(customerRef);
      const existingData = existingSnapshot.val();

      const leadData = {
        ...customerData,
        capturedAt: new Date().toISOString(),
        reason: reason,
        placedBy: user?.uid || 'unknown',
        products: cart.map(item => ({
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          unitPrice: item.mrp,
          totalPrice: item.mrp * item.quantity,
        })),
        totalAmount: getCartTotal(),
        totalItems: getCartItemCount(),
        lastUpdated: new Date().toISOString(),
        attempts: existingData ? (existingData.attempts || 0) + 1 : 1,
        status: 'lead'
      };

      // If existing data, update the attempts and last reason
      if (existingData) {
        leadData.firstCapturedAt = existingData.firstCapturedAt || existingData.capturedAt;
        leadData.reasonHistory = [...(existingData.reasonHistory || []), {
          reason: reason,
          timestamp: new Date().toISOString(),
          products: leadData.products,
          totalAmount: leadData.totalAmount
        }];
      } else {
        leadData.firstCapturedAt = leadData.capturedAt;
        leadData.reasonHistory = [{
          reason: reason,
          timestamp: new Date().toISOString(),
          products: leadData.products,
          totalAmount: leadData.totalAmount
        }];
      }

      await set(customerRef, leadData);
      console.log('Customer lead data saved:', reason);
    } catch (error) {
      console.error('Error saving customer lead data:', error);
    }
  };

  const handleRazorpayPayment = async (orderData, orderId) => {
    try {
      console.log('=== RAZORPAY PAYMENT LINK STARTED ===');
      console.log('Order ID:', orderId);
      console.log('Amount: ₹' + orderData.totalAmount);

      // Show loader while creating payment link
      setLoading(true);
      setLoadingMessage('Creating payment link...');

      // Validate amount
      if (orderData.totalAmount < 1) {
        showNotification('Minimum order amount is ₹1', '❌ Invalid Amount', 'error');
        setLoading(false);
        return;
      }

      // Create Payment Link using Firebase function
      console.log('Creating Razorpay payment link...');
      const paymentLinkResponse = await fetch('https://createpaymentlink-ph2yhuhnga-uc.a.run.app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: orderData.totalAmount,
          currency: 'INR',
          description: `Order #${orderId} - ${orderData.items.length} item(s)`,
          customer: {
            name: formData.name,
            email: formData.email || '',
            contact: formData.phone,
          },
          notify: {
            sms: true,
            email: formData.email ? true : false,
          },
          reminder_enable: true,
          notes: {
            orderId: orderId,
            customerName: formData.name,
            customerEmail: formData.email,
            customerPhone: formData.phone,
            totalItems: orderData.totalItems,
          }
        })
      });

      if (!paymentLinkResponse.ok) {
        const errorText = await paymentLinkResponse.text();
        throw new Error(`Payment link creation failed: ${errorText}`);
      }

      const paymentLinkResult = await paymentLinkResponse.json();

      if (!paymentLinkResult.success) {
        throw new Error(paymentLinkResult.message || 'Failed to create payment link');
      }

      console.log('✅ Payment link created:', paymentLinkResult.id);
      console.log('✅ Payment URL:', paymentLinkResult.short_url);

      // Don't save order to Firebase yet - wait for payment completion
      // Order will be saved in completeOrder() after payment is verified

      // Open payment link - works for web and Android WebView
      const paymentUrl = paymentLinkResult.short_url;

      // Detect if running in Android WebView
      const isAndroidWebView = /Android/.test(navigator.userAgent) &&
        /wv/.test(navigator.userAgent);

      if (isAndroidWebView) {
        // For Android WebView, try to open in external browser
        console.log('Opening payment link in external browser (Android WebView)');

        // Try window.open with _system first (Cordova/Capacitor)
        const opened = window.open(paymentUrl, '_system');

        // If _system doesn't work, try _blank
        if (!opened || opened === null) {
          window.open(paymentUrl, '_blank');
        }
      } else {
        // For regular web browsers, open in new tab
        console.log('Opening payment link in new tab (Web Browser)');
        window.open(paymentUrl, '_blank', 'noopener,noreferrer');
      }

      // Store payment link info in sessionStorage for tracking
      sessionStorage.setItem('pendingPayment', JSON.stringify({
        paymentLinkId: paymentLinkResult.id,
        orderId: orderId,
        orderData: orderData,
        timestamp: Date.now()
      }));

      // Keep loader visible - waiting for payment completion
      setLoadingMessage('Waiting for payment completion...');

      // Start polling for payment status
      console.log('Starting payment status polling...');
      let pollCount = 0;
      const maxPolls = 120; // 10 minutes (120 * 5 seconds)

      const pollInterval = setInterval(async () => {
        try {
          pollCount++;

          const statusResponse = await fetch('https://verifypaymentlink-ph2yhuhnga-uc.a.run.app', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              paymentLinkId: paymentLinkResult.id,
            })
          });

          if (statusResponse.ok) {
            const statusResult = await statusResponse.json();

            console.log(`Payment status check #${pollCount}:`, statusResult.status);

            if (statusResult.status === 'paid') {
              clearInterval(pollInterval);
              sessionStorage.removeItem('pendingPayment');

              console.log('✅ Payment completed successfully');

              // Update loading message
              setLoadingMessage('Processing order and generating invoice...');

              // Get payment details
              const payment = statusResult.payments && statusResult.payments.length > 0
                ? statusResult.payments[0]
                : {};

              // Payment details
              const paymentDetails = {
                payment_link_id: paymentLinkResult.id,
                payment_link_url: paymentLinkResult.short_url,
                razorpay_payment_id: payment.payment_id || '',
                payment_status: 'success',
                payment_timestamp: new Date().toISOString(),
                payment_method: payment.method || 'razorpay',
                amount_paid: orderData.totalAmount,
                currency: 'INR',
                verified: true,
                captured: true,
              };

              // Update order data
              const updatedOrderData = {
                ...orderData,
                paymentDetails: paymentDetails,
                paymentMethod: 'Online Payment (Razorpay)',
                status: 'Pending',
                paymentCompletedAt: new Date().toISOString()
              };

              // Complete order (includes commission distribution and invoice generation)
              await completeOrder(updatedOrderData, orderId);

              showNotification(
                `Payment successful! Invoice downloaded and commission distributed.`,
                `✅ Order Completed - ₹${orderData.totalAmount}`,
                'success'
              );

              // Reset form and hide loader after everything is complete
              resetForm();
              setLoading(false);
              setLoadingMessage('Processing your order...');
            } else if (statusResult.status === 'expired' || statusResult.status === 'cancelled') {
              clearInterval(pollInterval);
              sessionStorage.removeItem('pendingPayment');

              console.log('⚠️ Payment link expired or cancelled');

              const customerData = {
                name: orderData.customerName,
                email: orderData.customerEmail,
                phone: orderData.customerPhone,
                address: orderData.customerAddress.street,
                landmark: orderData.customerAddress.landmark,
                city: orderData.customerAddress.city,
                state: orderData.customerAddress.state,
                postalCode: orderData.customerAddress.postalCode,
                birthDate: orderData.birthDate,
                paymentMethod: 'Online Payment (Razorpay)',
              };

              await saveCustomerData(customerData, 'payment_cancelled');

              showNotification('Payment was cancelled or expired', '⚠️ Payment Cancelled', 'warning');
              setLoading(false);
              setLoadingMessage('Processing your order...');
            } else if (pollCount >= maxPolls) {
              // Timeout after 10 minutes
              clearInterval(pollInterval);
              console.log('⏱️ Payment polling timeout');
              showNotification(
                'Payment verification timeout. Please check your order status.',
                '⏱️ Verification Timeout',
                'warning'
              );
              setLoading(false);
              setLoadingMessage('Processing your order...');
            }
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          if (pollCount >= maxPolls) {
            clearInterval(pollInterval);
            setLoading(false);
          }
        }
      }, 5000); // Poll every 5 seconds

    } catch (error) {
      console.error('❌ Payment link error:', error);
      showNotification(`Failed to create payment link: ${error.message}`, '❌ Payment Error', 'error');
      setLoading(false);
    }
  };









  const handleWalletPayment = async (orderData, orderId) => {
    try {
      console.log('=== WALLET PAYMENT STARTED ===');
      console.log('Order ID:', orderId);
      console.log('Amount: ₹' + orderData.totalAmount);
      console.log('Current Wallet Balance: ₹' + walletBalance);

      const walletRef = ref(db, `HTAMS/users/${user.uid}/MySales`);

      // First check if wallet has sufficient balance
      const walletSnapshot = await get(walletRef);
      const currentBalance = Number(walletSnapshot.val()) || 0;

      if (currentBalance < orderData.totalAmount) {
        alert(`❌ Insufficient wallet balance\n\nRequired: ₹${orderData.totalAmount}\nAvailable: ₹${currentBalance}`);
        setLoading(false);
        return false;
      }

      // Deduct amount from wallet
      const transactionResult = await runTransaction(walletRef, (currentBalance) => {
        const balance = Number(currentBalance) || 0;
        if (balance >= orderData.totalAmount) {
          return balance - orderData.totalAmount;
        }
        // Abort transaction if insufficient balance
        return;
      });

      if (!transactionResult.committed) {
        alert('❌ Wallet payment failed. Transaction could not be completed.');
        setLoading(false);
        return false;
      }

      console.log('✅ Wallet amount deducted successfully');
      console.log('New Balance: ₹' + transactionResult.snapshot.val());

      // Add wallet payment details to order data
      const walletOrderData = {
        ...orderData,
        paymentMethod: 'Wallet Payment',
        paymentDetails: {
          payment_method: 'wallet',
          payment_status: 'success',
          payment_timestamp: new Date().toISOString(),
          amount_paid: orderData.totalAmount,
          wallet_balance_before: currentBalance,
          wallet_balance_after: transactionResult.snapshot.val(),
          verified: true,
          captured: true,
        },
        status: 'Pending',
        paymentCompletedAt: new Date().toISOString()
      };

      // Complete the order
      await completeOrder(walletOrderData, orderId);

      console.log('✅ Wallet payment completed successfully');
      return true;
    } catch (error) {
      console.error('❌ Wallet payment error:', error);
      alert(`❌ Wallet payment failed\n\nError: ${error.message}\n\nPlease try again.`);
      setLoading(false);
      return false;
    }
  };

  const completeOrder = async (orderData, orderId) => {
    try {
      console.log('=== COMPLETING ORDER ===');
      console.log('Order ID:', orderId);

      // Check if order exists to prevent duplicate processing
      const orderRef = ref(db, `HTAMS/orders/${orderId}`);
      const orderSnap = await get(orderRef);
      if (orderSnap.exists()) {
        console.log('⚠️ Order already processed. Skipping duplicate request.');
        resetForm();
        setLoading(false);
        return;
      }

      // STEP 1: Deduct stock from products (only after payment is confirmed)
      console.log('=== DEDUCTING STOCK ===');
      for (const item of cart) {
        const productStockRef = ref(db, `HTAMS/company/products/${item.id}/stock`);

        const transactionResult = await runTransaction(productStockRef, (currentStock) => {
          const stockNum = Number(currentStock) || 0;

          // Double-check stock availability during transaction
          if (stockNum < item.quantity) {
            console.error(`Insufficient stock for ${item.name}: ${stockNum} < ${item.quantity}`);
            return; // Abort transaction
          }

          return stockNum - item.quantity;
        });

        if (!transactionResult.committed) {
          throw new Error(`Failed to deduct stock for ${item.name}. Stock may have been sold out.`);
        }

        console.log(`✅ Stock deducted for ${item.name}: ${item.quantity} units`);
      }
      console.log('✅ All stock deducted successfully');

      // STEP 2: Save order to Firebase
      console.log('=== SAVING ORDER TO FIREBASE ===');
      const cleanOrderData = JSON.parse(JSON.stringify(orderData, (key, value) => {
        return value === undefined ? null : value;
      }));

      const orderDataWithId = {
        ...cleanOrderData,
        orderId: orderId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await set(ref(db, `HTAMS/orders/${orderId}`), orderDataWithId);
      console.log('✅ Order saved to Firebase');
    } catch (error) {
      console.error('❌ Error in completeOrder:', error);

      // If order saving fails, we should ideally restore the stock
      // But for now, we'll just throw the error
      throw error;
    }

    const customerRef = ref(db, `HTAMS/customers/${formData.phone}`);
    const customerSnap = await get(customerRef);

    const customerData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: orderData.customerAddress,
      birthDate: formData.birthDate,
    };

    if (customerSnap.exists()) {
      const existingOrders = customerSnap.val().myOrders || {};
      await update(customerRef, {
        ...customerData,
        myOrders: { ...existingOrders, [orderId]: true },
      });
    } else {
      const counterRef = ref(db, 'HTAMS/meta/customerIdCounter');
      const counterResult = await runTransaction(counterRef, (current) => (current || 1000) + 1);
      if (counterResult.committed) {
        const prefix = formData.email.slice(0, 3).toLowerCase();
        const customerId = `cus_${prefix}_${counterResult.snapshot.val()}`;
        await set(customerRef, {
          ...customerData,
          customerId: customerId,
          createdAt: Date.now(),
          myOrders: { [orderId]: true },
        });
      }
    }

    let commissionResults = [];
    for (const item of cart) {
      try {
        // Use the correct price field - prefer 'price' if available, fallback to 'mrp'
        const itemPrice = item.price || item.mrp || 0;
        const itemOrderData = {
          totalAmount: itemPrice * item.quantity,
          productId: item.id,
          productName: item.name,
          quantity: item.quantity,
          customerName: formData.name,
          customerPhone: formData.phone,
          customerEmail: formData.email,
        };

        console.log(`Processing commission for ${item.name}: ₹${itemOrderData.totalAmount}`);
        const commissionResult = await processCommission(itemOrderData, `${orderId}_${item.id}`);
        commissionResults.push({ productId: item.id, result: commissionResult });

        await saveSaleDetails(itemOrderData, `${orderId}_${item.id}`, commissionResult);
        console.log(`✅ Commission processed for ${item.name}`);
      } catch (error) {
        console.error(`❌ Commission processing failed for ${item.name}:`, error);
        commissionResults.push({ productId: item.id, result: { ok: false, error: error.message } });
      }
    }

    await generateMultiProductPdfBill({
      ...orderData,
      orderId: orderId,
      items: cart,
      saleDate: new Date().toLocaleString(),
    });

    const successfulCommissions = commissionResults.filter(r => r.result.ok).length;
    console.log(`Order placed successfully! Commission distributed for ${successfulCommissions} out of ${cart.length} products.`);
    resetForm();
  };

  const submitOrder = async () => {
    if (cart.length === 0) {
      alert('Please add items to cart before placing order');
      return;
    }

    if (!validateAllFields()) {
      alert('Please fix all validation errors before submitting');
      return;
    }

    setLoading(true);
    console.log('=== STARTING MULTI-PRODUCT ORDER SUBMISSION ===');

    // Save customer data immediately when order is placed
    const customerData = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      address: formData.address,
      landmark: formData.landmark,
      city: formData.city,
      state: formData.state,
      postalCode: formData.postalCode,
      birthDate: convertDDMMYYYYtoISO(formData.birthDate),

      paymentMethod: formData.paymentMethod,
    };

    await saveCustomerData(customerData, 'order_placed');

    if (!user?.uid) {
      console.error('=== USER VALIDATION ERROR ===');
      alert('User not logged in properly. Please refresh and try again.');
      setLoading(false);
      return;
    }

    try {
      // ONLY CHECK stock availability - don't deduct yet
      // Stock will be deducted in completeOrder() after successful payment
      console.log('=== CHECKING STOCK AVAILABILITY ===');
      for (const item of cart) {
        const productStockRef = ref(db, `HTAMS/company/products/${item.id}/stock`);
        const stockSnapshot = await get(productStockRef);
        const currentStock = Number(stockSnapshot.val()) || 0;

        console.log(`${item.name}: Available=${currentStock}, Required=${item.quantity}`);

        if (currentStock < item.quantity) {
          alert(`❌ Insufficient stock for ${item.name}\n\nAvailable: ${currentStock}\nRequired: ${item.quantity}`);
          setLoading(false);
          return;
        }
      }
      console.log('✅ All items have sufficient stock');

      const totalAmount = getCartTotal();
      const orderData = {
        items: cart.map(item => {
          // Use the correct price field - prefer 'price' if available, fallback to 'mrp'
          const itemPrice = item.price || item.mrp || 0;
          return {
            productId: item.id,
            productName: item.name,
            quantity: item.quantity,
            unitPrice: itemPrice,
            totalPrice: itemPrice * item.quantity,
            basePrice: item.basePrice || itemPrice,
          };
        }),
        totalAmount: totalAmount,
        totalItems: getCartItemCount(),
        orderDate: new Date().toISOString(),
        placedBy: user?.uid || 'unknown',
        customerName: formData.name,
        customerEmail: formData.email,
        customerPhone: formData.phone,
        customerAddress: {
          street: formData.address,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
        },
        birthDate: formData.birthDate,
        paymentMethod: formData.paymentMethod,
        status: 'pending',
      };



      // Generate sequential order ID
      const generateSequentialOrderId = async () => {
        const counterRef = ref(db, 'HTAMS/meta/orderIdCounter');
        const result = await runTransaction(counterRef, (current) => {
          return (current || 0) + 1;
        });

        const count = result.snapshot.val();
        // Format: order-01, order-02, ... order-10, ...
        return `order-${String(count).padStart(2, '0')}`;
      };

      // Generate order ID
      const orderId = await generateSequentialOrderId();

      if (formData.paymentMethod === 'Wallet' && totalAmount <= walletBalance) {
        const success = await handleWalletPayment(orderData, orderId);
        if (success) {
          alert('Order placed successfully using wallet balance! Invoice is downloading.');
          resetForm();
        }
        setLoading(false);
      } else if (formData.paymentMethod === 'Online') {
        // Don't hide loading here - payment verification will handle it
        await handleRazorpayPayment(orderData, orderId);
      } /* else if (formData.paymentMethod === 'Cash') {
          // Handle cash payment - complete order directly
          const updatedOrderData = {
            ...orderData,
            status: 'pending',
            paymentDetails: {
              payment_method: 'Cash',
              payment_status: 'pending_collection',
              payment_timestamp: new Date().toISOString()
            }
          };
          await completeOrder(updatedOrderData, orderId);
          alert('Order placed successfully! Cash payment will be collected on delivery. Invoice is downloading.');
          resetForm();
          setLoading(false);
      } */ else {
        alert('Please select a valid payment method.');
        setLoading(false);
      }

    } catch (err) {
      console.error('=== MULTI-PRODUCT ORDER PROCESSING ERROR ===', err);
      alert('Failed to place order. An error occurred. Check console for details.');
      setLoading(false);
    }
  };

  const resetForm = () => {
    setShowCartModal(false);
    setFormData(initialFormData);
    setValidationErrors({});
    setSelectedCustomer('');
    setIsNewCustomer(true);
    setCustomerOrders([]);
    setAvailableDistricts([]);
    clearCart();
  };

  // Save customer interest for follow-up
  // const saveCustomerInterest = async (customerData, cartItems) => {
  //   try {
  //     const interestId = `interest_${Date.now()}_${customerData.phone}`;
  //     const totalInterestValue = cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);

  //     const interestData = {
  //       name: customerData.name,
  //       phone: customerData.phone,
  //       email: customerData.email,
  //       interestDate: new Date().toISOString(),
  //       interestedProducts: cartItems.map(item => ({
  //         name: item.name,
  //         price: item.price,
  //         quantity: item.quantity,
  //         productId: item.id
  //       })),
  //       totalInterestValue: totalInterestValue,
  //       followUpDays: customerData.followUpDays || 2,
  //       status: 'pending',
  //       notes: customerData.notes || '',
  //       source: 'cart_abandonment'
  //     };

  //     const interestRef = ref(db, `HTAMS/followUpCustomers/${interestId}`);
  //     await set(interestRef, interestData);

  //     return true;
  //   } catch (error) {
  //     console.error('Error saving customer interest:', error);
  //     return false;
  //   }
  // };

  // Handle interest form submission
  // const handleInterestSubmit = async (e) => {
  //   e.preventDefault();

  //   if (!interestFormData.name || !interestFormData.phone) {
  //     alert('Please fill in name and phone number');
  //     return;
  //   }

  //   setLoading(true);
  //   try {
  //     const success = await saveCustomerInterest(interestFormData, cart);
  //     if (success) {
  //       alert('Your interest has been saved! We will follow up with you soon.');
  //       setShowInterestModal(false);
  //       setInterestFormData({
  //         name: '',
  //         phone: '',
  //         email: '',
  //         followUpDays: 2,
  //         notes: ''
  //       });
  //       clearCart();
  //     } else {
  //       alert('Failed to save your interest. Please try again.');
  //     }
  //   } catch (error) {
  //     console.error('Error submitting interest:', error);
  //     alert('An error occurred. Please try again.');
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Simple cart close handler
  const handleCartClose = () => {
    setShowCartModal(false);
  };

  // Save follow-up using current form data and cart items
  const handleSaveFollowUp = async () => {
    try {
      if (cart.length === 0) {
        alert('Please add some products to the cart before saving a follow-up.');
        return;
      }

      // Require same information as order so later autofill works
      if (!validateAllFields()) {
        alert('Please complete customer details before saving follow-up.');
        return;
      }

      if (!user?.uid) {
        alert('User not logged in. Please refresh and try again.');
        return;
      }

      setLoading(true);

      const followUpDays = 2;
      const scheduledFollowUpAt = new Date(Date.now() + followUpDays * 24 * 60 * 60 * 1000).toISOString();
      const interestId = `interest_${Date.now()}_${formData.phone}`;
      const totalInterestValue = cart.reduce((t, it) => t + (it.price * it.quantity), 0);

      const interestData = {
        id: interestId,
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: {
          street: formData.address,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
        },
        birthDate: formData.birthDate,
        interestedProducts: cart.map(item => ({
          productId: item.id,
          name: item.name,
          price: item.mrp,
          quantity: item.quantity,
          totalPrice: item.mrp * item.quantity,
        })),
        totalInterestValue,
        totalItems: getCartItemCount(),
        followUpDays,
        scheduledFollowUpAt,
        status: 'pending',
        notes: '',
        placedBy: user.uid,
        capturedAt: new Date().toISOString(),
        source: 'follow_up_from_cart'
      };

      // Save follow-up entry
      await set(ref(db, `HTAMS/followUpCustomers/${interestId}`), interestData);

      // Upsert prospect into customers to enable autofill later
      const customerRef = ref(db, `HTAMS/customers/${formData.phone}`);
      const existingSnap = await get(customerRef);
      const prospectData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: {
          street: formData.address,
          landmark: formData.landmark,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
        },
        birthDate: formData.birthDate,
        createdBy: user.uid,
        ownerId: user.uid,
        prospect: true,
        lastFollowUpId: interestId,
        updatedAt: new Date().toISOString(),
      };

      if (existingSnap.exists()) {
        await update(customerRef, prospectData);
      } else {
        await set(customerRef, { ...prospectData, createdAt: new Date().toISOString() });
      }

      showNotification('Follow-up saved', `📌 ${formData.name} in ${cart.length} item(s)`, 'success');
      alert('Follow-up saved. We will remind you on the scheduled day.');
    } catch (e) {
      console.error('Error saving follow-up:', e);
      alert('Failed to save follow-up. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle follow up button click
  // const handleFollowUpClick = () => {
  //   if (cart.length === 0) {
  //     alert('Please add some products to your interest list first!');
  //     return;
  //   }
  //   setShowInterestModal(true);
  // };

  const saveSaleDetails = async (orderData, orderId, commissionResult) => {
    try {
      let saleData;

      if (commissionResult?.ok && commissionResult?.paidTo) {
        const commissionsObject = {};

        commissionResult.paidTo.forEach(recipient => {
          commissionsObject[recipient.uid] = {
            amount: recipient.amount,
            diff: recipient.diff,
            rate: recipient.rate,
            role: recipient.role,
          };
        });

        const sellerInfo = commissionResult.paidTo.find(p => p.uid === (user?.uid || 'unknown'));
        const roleAtSale = sellerInfo?.role || user?.currentLevel || null;

        saleData = {
          sellerId: user?.uid || 'unknown',
          amount: orderData.totalAmount,
          product: {
            id: orderData.productId,
            name: orderData.productName,
          },
          orderId: orderId,
          roleAtSale: roleAtSale,
          timestamp: Date.now(),
          commissions: commissionsObject,
          customerName: orderData.customerName,
          customerPhone: orderData.customerPhone,
          customerEmail: orderData.customerEmail,
          quantity: orderData.quantity,
          unitPrice: orderData.totalAmount / orderData.quantity,
          paymentMethod: orderData.paymentMethod || 'Online Payment (Razorpay)',
          saleDate: new Date().toISOString(),
          status: 'Pending',
          commissionDistributed: true,
        };
      } else {
        saleData = {
          sellerId: user?.uid || 'unknown',
          amount: orderData.totalAmount,
          product: {
            id: orderData.productId,
            name: orderData.productName,
          },
          orderId: orderId,
          roleAtSale: user?.currentLevel || null,
          timestamp: Date.now(),
          commissions: {},
          customerName: orderData.customerName,
          customerPhone: orderData.customerPhone,
          customerEmail: orderData.customerEmail,
          quantity: orderData.quantity,
          unitPrice: orderData.totalAmount / orderData.quantity,
          paymentMethod: orderData.paymentMethod || 'Online Payment (Razorpay)',
          saleDate: new Date().toISOString(),
          status: 'Pending',
          commissionDistributed: false,
          commissionError: commissionResult?.error || 'Commission processing failed',
        };
      }

      console.log('Saving sale details:', saleData);
      const saleRef = ref(db, `HTAMS/salesDetails/${orderId}`);
      await set(saleRef, saleData);

      console.log('Sale details saved successfully');
      return saleData;
    } catch (error) {
      console.error('Error saving sale details:', error);
      throw error;
    }
  };

  const processCommission = async (orderData, orderId) => {
    console.log('=== STARTING COMMISSION PROCESSING ===');

    try {
      const apiPayload = {
        sellerId: user?.uid || 'unknown',
        amount: orderData.totalAmount,
        product: {
          id: orderData.productId,
          name: orderData.productName,
        },
        orderId: orderId,
        orderId: orderId,
        idempotencyKey: `order_${orderId}`,
      };

      console.log('=== API PAYLOAD ===', apiPayload);

      const response = await fetch('https://processsale-ph2yhuhnga-uc.a.run.app/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      console.log('Response status:', response.status);
      const responseText = await response.text();
      console.log('Raw response:', responseText);

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status} - ${responseText}`);
      }

      const result = JSON.parse(responseText);
      console.log('Parsed result:', result);
      return result;

    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  };

  const generateMultiProductPdfBill = async (billData) => {
    try {
      const doc = new jsPDF();
      const address = billData.customerAddress;
      const fullAddress = `${address.street}${address.landmark ? `, ${address.landmark}` : ''}, ${address.city}, ${address.state} - ${address.postalCode}`;

      // Page dimensions
      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;

      // Generate invoice details
      const timestamp = Date.now().toString();
      const invoiceNo = `${timestamp.slice(0, 5)}-${timestamp.slice(5, 7)}`;
      const invoiceDate = new Date();
      const formattedDate = invoiceDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      const formattedTime = invoiceDate.toLocaleTimeString('en-US', { weekday: 'long', hour: '2-digit', minute: '2-digit' });

      // CORRECT GST CALCULATION - Invoice Price is GST-INCLUSIVE
      // If Invoice Price = 100, and GST = 5%, then:
      // Amount (base) = 100 / 1.05 = 95.24
      // GST = 100 - 95.24 = 4.76
      // Total = 95.24 + 4.76 = 100
      const GST_RATE = 0.05; // 5% GST

      // Calculate total invoice price (GST-inclusive)
      const totalInvoicePrice = billData.items.reduce((total, item) => {
        return total + (item.invoicePrice * item.quantity);
      }, 0);

      // Calculate base amount (excluding GST) from invoice price
      // Formula: Base = Invoice Price / (1 + GST Rate)
      const baseAmount = totalInvoicePrice / (1 + GST_RATE);

      // Calculate GST amount
      const gstAmount = totalInvoicePrice - baseAmount;

      // Total amount is the invoice price (GST-inclusive)
      const totalAmount = totalInvoicePrice;

      const isIntraState = address.state === 'Maharashtra';
      const sgstAmount = isIntraState ? gstAmount / 2 : 0;
      const cgstAmount = isIntraState ? gstAmount / 2 : 0;
      const igstAmount = isIntraState ? 0 : gstAmount;

      // Background
      doc.setFillColor(240, 244, 232);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');

      // Logo
      try {
        doc.addImage(panchgiriLogo, 'jpg', (pageWidth - 30) / 2, 15, 30, 30);
      } catch (e) { console.warn('Logo error'); }

      // Title
      doc.setFontSize(36);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 26);
      doc.text('INVOICE', pageWidth / 2, 55, { align: 'center' });

      // Invoice details
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 85, 104);
      doc.text(`No. ${invoiceNo}`, margin, 68);
      doc.text(`GSTIN: 27AISPB8262F2ZO`, margin, 74);
      doc.text(formattedDate, pageWidth - margin, 68, { align: 'right' });
      doc.text(formattedTime, pageWidth - margin, 74, { align: 'right' });

      // Customer
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(45, 55, 72);
      doc.text('INVOICE TO:', margin, 86);

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 26);
      doc.text(billData.customerName, pageWidth - margin, 86, { align: 'right' });

      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 85, 104);
      doc.text(`Mobile: ${billData.customerPhone}`, pageWidth - margin, 93, { align: 'right' });
      doc.text(`Address: ${fullAddress}`, pageWidth - margin, 99, { align: 'right', maxWidth: 100 });

      // TABLE WITH SR NO.
      const tableStartY = 110;
      const tableHeaderHeight = 10;

      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, tableStartY, pageWidth - (2 * margin), tableHeaderHeight, 2, 2, 'F');

      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(45, 55, 72);
      doc.text('SR', margin + 8, tableStartY + 8);
      doc.text('DESCRIPTION', margin + 22, tableStartY + 8);
      doc.text('PRICE', pageWidth - 110, tableStartY + 8, { align: 'center' });
      doc.text('QTY', pageWidth - 65, tableStartY + 8, { align: 'center' });
      doc.text('TOTAL', pageWidth - margin - 10, tableStartY + 8, { align: 'right' });

      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(1);
      doc.line(margin + 5, tableStartY + tableHeaderHeight, pageWidth - margin - 5, tableStartY + tableHeaderHeight);

      // Table rows
      let currentY = tableStartY + tableHeaderHeight + 6;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 85, 104);

      billData.items.forEach((item, index) => {
        if (currentY > pageHeight - 70) {
          doc.addPage();
          doc.setFillColor(240, 244, 232);
          doc.rect(0, 0, pageWidth, pageHeight, 'F');
          currentY = 30;
        }

        // Line total = invoice price (GST-inclusive) × quantity
        const lineTotal = item.invoicePrice * item.quantity;

        // Sr No.
        doc.text((index + 1).toString(), margin + 10, currentY);

        // Description
        doc.text(item.name, margin + 22, currentY, { maxWidth: 75 });

        // Price (invoice price - GST-inclusive)
        doc.text('Rs.' + item.invoicePrice.toFixed(2), pageWidth - 110, currentY, { align: 'center' });

        // Qty
        doc.setFont('helvetica', 'bold');
        doc.text(item.quantity.toString(), pageWidth - 65, currentY, { align: 'center' });

        // TOTAL = invoice price × quantity (GST-inclusive)
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(26, 26, 26);
        doc.text('Rs.' + lineTotal.toFixed(2), pageWidth - margin - 10, currentY, { align: 'right' });

        doc.setFont('helvetica', 'normal');
        doc.setTextColor(74, 85, 104);

        currentY += 10;

        if (index < billData.items.length - 1) {
          doc.setDrawColor(230, 235, 240);
          doc.setLineWidth(0.3);
          doc.line(margin + 10, currentY - 6, pageWidth - margin - 10, currentY - 6);
        }
      });

      // TOTALS — MATCHING YOUR SCREENSHOT
      const totalsY = currentY + 6;
      doc.setDrawColor(203, 213, 224);
      doc.setLineWidth(1.5);
      doc.line(margin + 5, totalsY, pageWidth - margin - 5, totalsY);

      // Amount = (base total only)
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(45, 55, 72);
      doc.text('Amount =', pageWidth - margin - 75, totalsY + 8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(26, 26, 26);
      doc.text('Rs.' + baseAmount.toFixed(2), pageWidth - margin - 5, totalsY + 8, { align: 'right' });

      // GST
      let currentLineY = totalsY + 15;

      if (isIntraState) {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 55, 72);
        doc.text('SGST (2.5%)', pageWidth - margin - 75, currentLineY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(26, 26, 26);
        doc.text('Rs.' + sgstAmount.toFixed(2), pageWidth - margin - 5, currentLineY, { align: 'right' });

        currentLineY += 7;
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 55, 72);
        doc.text('CGST (2.5%)', pageWidth - margin - 75, currentLineY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(26, 26, 26);
        doc.text('Rs.' + cgstAmount.toFixed(2), pageWidth - margin - 5, currentLineY, { align: 'right' });
      } else {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(45, 55, 72);
        doc.text('IGST (5%)', pageWidth - margin - 75, currentLineY);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(26, 26, 26);
        doc.text('Rs.' + igstAmount.toFixed(2), pageWidth - margin - 5, currentLineY, { align: 'right' });
      }

      // Final Total
      const totalLineY = currentLineY + 4;
      doc.setDrawColor(203, 213, 224);
      doc.setLineWidth(1);
      doc.line(pageWidth - margin - 80, totalLineY, pageWidth - margin, totalLineY);

      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(45, 55, 72);
      doc.text('Total Amount', pageWidth - margin - 75, totalLineY + 9);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(26, 26, 26);
      doc.text('Rs.' + totalAmount.toFixed(2), pageWidth - margin - 5, totalLineY + 9, { align: 'right' });

      // Amount in words
      const amountInWords = convertAmountToWords(Math.floor(totalAmount));
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(74, 85, 104);
      doc.text(amountInWords, pageWidth - margin - 5, totalLineY + 23, { align: 'right', maxWidth: 100 });

      // Footer (unchanged)
      const footerY = totalLineY + 23;
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(45, 55, 72);
      doc.text('SEND PAYMENTS TO:', margin + 5, footerY);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 85, 104);
      doc.text(billData.customerName, margin + 5, footerY + 8);
      doc.text(billData.customerEmail, margin + 5, footerY + 15);

      doc.setFontSize(30);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(74, 124, 89);
      doc.text('Thank You!', pageWidth - margin - 5, footerY + 12, { align: 'right' });

      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Payment: ${billData.paymentMethod} | Items: ${billData.totalItems}`, pageWidth / 2, pageHeight - 10, { align: 'center' });

      // Save & Upload (unchanged)
      const fileName = `Invoice_${invoiceNo}_${billData.customerName.replace(/\s+/g, '_')}.pdf`;
      const pdfBlob = doc.output('blob');
      doc.save(fileName);

      showNotification(`Invoice downloaded successfully`, `File: ${fileName}`, 'success');

      // Firebase upload (your original logic)
      try {
        const orderId = billData.orderId || generateOrderId();
        const storagePath = `orders/${orderId}/invoice/${fileName}`;
        const pdfStorageRef = storageRef(storage, storagePath);

        await uploadBytes(pdfStorageRef, pdfBlob, { contentType: 'application/pdf' });
        const downloadURL = await getDownloadURL(pdfStorageRef);

        await set(ref(db, `HTAMS/orders/${orderId}/invoice`), {
          invoiceNumber: invoiceNo,
          totalAmount,
          baseAmount,
          gstAmount,
          downloadURL,
          fileName,
          generatedAt: new Date().toISOString(),
          // ... your other fields
        });

        showNotification(`Invoice uploaded`, fileName, 'success');
      } catch (err) {
        console.error(err);
        showNotification(`Upload failed`, '', 'error');
      }

    } catch (error) {
      console.error('Error:', error);
      alert('Failed to generate invoice');
    }
  };




  const convertAmountToWords = (amount) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (amount === 0) return 'Zero Rupees Only';

    let words = '';
    let crores = Math.floor(amount / 10000000);
    let lakhs = Math.floor((amount % 10000000) / 100000);
    let thousands_part = Math.floor((amount % 100000) / 1000);
    let hundreds = Math.floor((amount % 1000) / 100);
    let remainder = amount % 100;

    if (crores > 0) {
      words += convertNumberToWords(crores) + ' Crore ';
    }
    if (lakhs > 0) {
      words += convertNumberToWords(lakhs) + ' Lakh ';
    }
    if (thousands_part > 0) {
      words += convertNumberToWords(thousands_part) + ' Thousand ';
    }
    if (hundreds > 0) {
      words += ones[hundreds] + ' Hundred ';
    }
    if (remainder > 0) {
      if (remainder < 10) {
        words += ones[remainder];
      } else if (remainder < 20) {
        words += teens[remainder - 10];
      } else {
        words += tens[Math.floor(remainder / 10)];
        if (remainder % 10 > 0) {
          words += ' ' + ones[remainder % 10];
        }
      }
    }

    return words.trim() + ' Rupees Only';
  };

  const convertNumberToWords = (num) => {
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

    if (num < 10) return ones[num];
    if (num < 20) return teens[num - 10];
    if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 > 0 ? ' ' + ones[num % 10] : '');
    return '';
  };


  return (
    <div className="all-products-container">
      {/* Top Right Notifications Stack */}
      <div
        style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          maxWidth: '420px',
          pointerEvents: 'none'
        }}
      >
        {notifications.map((notification, index) => {
          const getNotificationStyle = (type) => {
            switch (type) {
              case 'success':
                return {
                  bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '#10b981',
                  shadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
                  iconBg: 'rgba(16, 185, 129, 0.1)',
                  progressBg: '#10b981'
                };
              case 'error':
                return {
                  bg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  border: '#ef4444',
                  shadow: '0 10px 40px rgba(239, 68, 68, 0.3)',
                  iconBg: 'rgba(239, 68, 68, 0.1)',
                  progressBg: '#ef4444'
                };
              case 'warning':
                return {
                  bg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  border: '#f59e0b',
                  shadow: '0 10px 40px rgba(245, 158, 11, 0.3)',
                  iconBg: 'rgba(245, 158, 11, 0.1)',
                  progressBg: '#f59e0b'
                };
              case 'info':
                return {
                  bg: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  border: '#3b82f6',
                  shadow: '0 10px 40px rgba(59, 130, 246, 0.3)',
                  iconBg: 'rgba(59, 130, 246, 0.1)',
                  progressBg: '#3b82f6'
                };
              default:
                return {
                  bg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  border: '#10b981',
                  shadow: '0 10px 40px rgba(16, 185, 129, 0.3)',
                  iconBg: 'rgba(16, 185, 129, 0.1)',
                  progressBg: '#10b981'
                };
            }
          };

          const style = getNotificationStyle(notification.type);

          return (
            <div
              key={notification.id}
              style={{
                animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                animationDelay: `${index * 0.1}s`,
                animationFillMode: 'both',
                pointerEvents: 'auto'
              }}
            >
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.98)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  boxShadow: style.shadow,
                  border: `2px solid ${style.border}`,
                  overflow: 'hidden',
                  minWidth: '340px',
                  maxWidth: '420px',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                  e.currentTarget.style.boxShadow = `0 15px 50px rgba(0, 0, 0, 0.2)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = style.shadow;
                }}
              >
                {/* Progress Bar */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: '4px',
                    width: '100%',
                    background: '#f1f5f9',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      height: '100%',
                      background: style.progressBg,
                      animation: 'progressBar 3s linear',
                      transformOrigin: 'left'
                    }}
                  />
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    padding: '18px 20px',
                    gap: '14px'
                  }}
                >
                  {/* Icon with animated background */}
                  <div
                    style={{
                      width: '48px',
                      height: '48px',
                      background: style.bg,
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '20px',
                      flexShrink: 0,
                      boxShadow: `0 4px 12px ${style.iconBg}`,
                      animation: 'pulse 2s ease-in-out infinite'
                    }}
                  >
                    <FaShoppingCart />
                  </div>

                  {/* Content */}
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      paddingTop: '2px'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '15px',
                        fontWeight: '700',
                        color: '#0f172a',
                        marginBottom: '6px',
                        lineHeight: '1.3',
                        letterSpacing: '-0.01em'
                      }}
                    >
                      {notification.title}
                    </div>
                    <div
                      style={{
                        fontSize: '13px',
                        color: '#64748b',
                        lineHeight: '1.5',
                        fontWeight: '500'
                      }}
                    >
                      {notification.message}
                    </div>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                    style={{
                      width: '28px',
                      height: '28px',
                      border: 'none',
                      background: '#f1f5f9',
                      borderRadius: '8px',
                      color: '#64748b',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '13px',
                      flexShrink: 0,
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#e2e8f0';
                      e.currentTarget.style.transform = 'rotate(90deg)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#f1f5f9';
                      e.currentTarget.style.transform = 'rotate(0deg)';
                    }}
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(100%) scale(0.8);
          }
          to {
            opacity: 1;
            transform: translateX(0) scale(1);
          }
        }

        @keyframes progressBar {
          from {
            transform: scaleX(1);
          }
          to {
            transform: scaleX(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.05);
          }
        }
      `}</style>

      {/* Keep the original CartNotification commented out for now
      {notification && (
        <CartNotification
          message={notification.message}
          title={notification.title}
          onClose={() => setNotification(null)}
          onViewCart={() => setShowCartModal(true)}
        />
      )}
      */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <span className="title-icon">🛍️</span>
            All Products
          </h1>
          <p className="page-subtitle">Browse and purchase our available products</p>
        </div>

        <div className="product-stats">
          <div className="stat-item cart-stat" onClick={() => setShowCartModal(true)}>
            <span className="stat-number">{getCartItemCount()}</span>
            <span className="stat-label" style={{ color: 'white' }}>Items in Cart</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">₹{walletBalance.toLocaleString()}</span>
            <span className="stat-label" style={{ color: 'white' }}>Wallet Balance</span>
          </div>
        </div>
      </div>

      <div className="search-section">
        <div className="search-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, price, or serial number..."
            className="search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="clear-search" onClick={() => setSearchQuery('')}>
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {
        cart.length > 0 && (
          <div className="cart-summary-bar">
            <div className="cart-summary-content">
              <span className="cart-items-count">
                {getCartItemCount()} items in cart
              </span>
              <span className="cart-total">
                Total: ₹{getCartTotal().toLocaleString()}
              </span>
              <div className="cart-actions">

                <button
                  className="view-cart-button"
                  onClick={() => setShowCartModal(true)}
                >
                  <FaShoppingCart />
                  View Cart & Checkout
                </button>
              </div>
            </div>
          </div>
        )
      }

      <div className="products-grid">
        {filteredProducts.length === 0 ? (
          <div className="no-products">
            <span className="no-products-icon">📦</span>
            <p>
              {searchQuery
                ? "No available products match your search."
                : "No products are currently in stock."
              }
            </p>
          </div>
        ) : (
          filteredProducts.map((prod) => (
            <div key={prod.id} className="product-card" data-product-id={prod.id}>
              <div className="product-image-container" onClick={() => { setSelectedProduct(prod); setShowImageModal(true); }} style={{ cursor: 'pointer' }}>
                <img src={prod.imageUrl} alt={prod.name} className="product-image" />
              </div>

              <div className="product-details">
                <h3 className="product-name">{prod.name}</h3>
                <p className="product-category">{prod.category}</p>
                <p className="product-description">{prod.sr}</p>
                <div className="product-pricing">
                  <span className="product-price">₹{prod.mrp?.toLocaleString()}</span>
                  <span className="stock-info">
                    Stock: {prod.stock}
                  </span>
                </div>
              </div>
              <div className="product-actions">
                {(() => {
                  const cartItem = cart.find(item => item.id === prod.id);
                  const quantity = cartItem?.quantity || 0;

                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                      {quantity > 0 ? (
                        // Show quantity controls when item is in cart
                        <div
                          className="quantity-controls"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px',
                            background: '#f8fafc',
                            border: '2px solid #e2e8f0',
                            borderRadius: '8px',
                            padding: '8px 12px'
                          }}
                        >
                          <button
                            className="quantity-btn decrease"
                            onClick={() => updateCartQuantity(prod.id, quantity - 1)}
                            disabled={loading}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              background: 'white',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '14px',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                              color: '#dc2626'
                            }}
                          >
                            <FaMinus />
                          </button>
                          <span
                            className="quantity-display"
                            style={{
                              fontSize: '16px',
                              fontWeight: '600',
                              color: '#1e293b',
                              minWidth: '24px',
                              textAlign: 'center'
                            }}
                          >
                            {quantity}
                          </span>
                          <button
                            className="quantity-btn increase"
                            onClick={() => updateCartQuantity(prod.id, quantity + 1)}
                            disabled={loading || quantity >= prod.stock}
                            style={{
                              width: '32px',
                              height: '32px',
                              border: 'none',
                              background: 'white',
                              borderRadius: '6px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              fontSize: '14px',
                              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                              color: '#10b981'
                            }}
                          >
                            <FaPlus />
                          </button>
                        </div>
                      ) : (
                        // Show Add to Cart button when item not in cart
                        <button
                          className="add-to-cart-button"
                          onClick={() => addToCart(prod)}
                          disabled={loading || prod.stock === 0}
                        >
                          <FaShoppingCart />
                          Add  Cart
                        </button>
                      )}

                      {/* Buy Now button - ALWAYS VISIBLE */}
                      <button
                        className="buy-now-button"
                        onClick={() => handleBuyNow(prod)}
                        disabled={loading || prod.stock === 0}
                        style={{
                          width: '100%',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          color: 'white',
                          border: 'none',
                          padding: '6px 8px',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #d97706 0%, #b45309 100%)';
                          e.target.style.transform = 'translateY(-2px)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.background = 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
                          e.target.style.transform = 'translateY(0px)';
                        }}
                      >
                        🛒 Buy
                      </button>
                    </div>
                  );
                })()}

                {/* <div className="product-stock">
    {prod.stock} available
  </div> */}
              </div>


            </div>
          ))
        )}
      </div>

      {
        showCartModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <div className="modal-header">
                <h2>Shopping Cart ({getCartItemCount()} items)</h2>
                <button className="close-button" onClick={handleCartClose} disabled={loading}>
                  <FaTimes />
                </button>
              </div>

              <div className="modal-content">
                {/* Loading removed from here to be full screen */}

                {cart.length === 0 ? (
                  <div className="empty-cart">
                    <span className="empty-cart-icon">🛒</span>
                    <p>Your cart is empty</p>
                    <button className="continue-shopping" onClick={() => setShowCartModal(false)}>
                      Continue Shopping
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="cart-items-section">
                      <h3>Items in Your Cart</h3>
                      <div className="cart-items-list">
                        {cart.map((item) => (
                          <div key={item.id} className="cart-item">
                            <div className="cart-item-image">
                              <img src={item.imageUrl} alt={item.name} />
                            </div>
                            <div className="cart-item-details">
                              <h4>{item.name}</h4>
                              <p className="cart-item-price">₹{item.mrp.toLocaleString()} each</p>
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px',
                                flexShrink: 0,
                                flexWrap: 'nowrap'
                              }}
                            >
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '12px',
                                  background: '#f8fafc',
                                  border: '2px solid #e2e8f0',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  minWidth: '120px',
                                  visibility: 'visible'
                                }}
                              >
                                <button
                                  onClick={() => updateCartQuantity(item.id, item.quantity - 1)}
                                  disabled={loading}
                                  className="quantity-btn decrease"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    border: 'none',
                                    background: 'white',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    color: '#dc2626'
                                  }}
                                >
                                  <FaMinus />
                                </button>
                                <span
                                  className="quantity-display"
                                  style={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: '#1e293b',
                                    minWidth: '24px',
                                    textAlign: 'center'
                                  }}
                                >
                                  {item.quantity}
                                </span>
                                <button
                                  onClick={() => updateCartQuantity(item.id, item.quantity + 1)}
                                  disabled={loading}
                                  className="quantity-btn increase"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    border: 'none',
                                    background: 'white',
                                    borderRadius: '6px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                    color: '#10b981'
                                  }}
                                >
                                  <FaPlus />
                                </button>
                              </div>
                              <div className="cart-item-total">
                                ₹{(item.mrp * item.quantity).toLocaleString()}
                              </div>
                              <button
                                onClick={() => removeFromCart(item.id)}
                                disabled={loading}
                                className="remove-button"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  border: 'none',
                                  background: '#fee2e2',
                                  color: '#dc2626',
                                  borderRadius: '6px',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="cart-total-section">
                        <div className="cart-total-row">
                          <span className="cart-total-label">Total Amount:</span>
                          <span className="cart-total-amount">₹{getCartTotal().toLocaleString()}</span>
                        </div>
                        <button
                          className="clear-cart-button"
                          onClick={clearCart}
                          disabled={loading}
                        >
                          Clear Cart
                        </button>
                      </div>
                    </div>

                    <form className="customer-form" onSubmit={(e) => { e.preventDefault(); submitOrder(); }}>
                      <h3>Customer Information</h3>

                      {/* Customer Selection Dropdown */}
                      <div className="form-group full-width customer-selection">
                        <label>Select Customer</label>
                        <select
                          value={selectedCustomer}
                          onChange={handleCustomerSelection}
                          disabled={loading}
                          className="customer-select"
                        >
                          <option value="">Choose existing customer or add new</option>
                          <option value="new">➕ Add New Customer</option>
                          {customers.length > 0 && (
                            <optgroup label="Existing Customers">
                              {customers
                                .filter((c) => {
                                  if (!user?.uid) return false;
                                  const ownedField = c.createdBy === user.uid || c.ownerId === user.uid || c.placedBy === user.uid;
                                  const ownedFromOrders = myCustomerPhones.has(c.phone);
                                  return ownedField || ownedFromOrders;
                                })
                                .map((customer) => (
                                  <option key={customer.phone} value={customer.phone}>
                                    👤 {customer.displayName}
                                  </option>
                                ))}
                            </optgroup>
                          )}
                        </select>
                        <div className="customer-selection-hint">
                          {isNewCustomer ? (
                            <span className="hint-new">🆕 Fill details for new customer</span>
                          ) : (
                            <span className="hint-existing">✅ Customer details loaded automatically</span>
                          )}
                        </div>
                      </div>

                      {/* Customer Previous Orders - HIDDEN */}
                      {/* Order history display removed as per requirement */}

                      <div className="form-grid">
                        <div className="form-group full-width">
                          <label>Full Name *</label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleNameInput}
                            required
                            placeholder="Enter  full name"
                            disabled={loading}
                            className={validationErrors.name ? 'error' : ''}
                          />
                          {validationErrors.name && (
                            <span className="error-message">{validationErrors.name}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Email Address *</label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleFormChange}
                            required
                            placeholder="Enter  email"
                            disabled={loading}
                            className={validationErrors.email ? 'error' : ''}
                          />
                          {validationErrors.email && (
                            <span className="error-message">{validationErrors.email}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Phone Number *</label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handlePhoneInput}
                            required
                            placeholder="Enter 10-digit mobile number"
                            disabled={loading}
                            maxLength="10"
                            className={validationErrors.phone ? 'error' : ''}
                          />
                          {validationErrors.phone && (
                            <span className="error-message">{validationErrors.phone}</span>
                          )}
                        </div>

                        <div className="form-group full-width">
                          <label>Street Address *</label>
                          <textarea
                            name="address"
                            value={formData.address}
                            onChange={handleFormChange}
                            required
                            placeholder="Enter complete address"
                            rows="2"
                            disabled={loading}
                            className={validationErrors.address ? 'error' : ''}
                          />
                          {validationErrors.address && (
                            <span className="error-message">{validationErrors.address}</span>
                          )}
                        </div>

                        <div className="form-group full-width">
                          <label>Landmark (Optional)</label>
                          <input
                            type="text"
                            name="landmark"
                            value={formData.landmark}
                            onChange={handleFormChange}
                            placeholder="Enter nearby landmark"
                            disabled={loading}
                          />
                        </div>

                        <div className="form-group">
                          <label>State *</label>
                          <select
                            name="state"
                            value={formData.state}
                            onChange={handleStateChange}
                            required
                            disabled={loading}
                            className={validationErrors.state ? 'error' : ''}
                          >
                            <option value="">Select your state</option>
                            {Object.keys(statesAndDistricts).map(state => (
                              <option key={state} value={state}>{state}</option>
                            ))}
                          </select>
                          {validationErrors.state && (
                            <span className="error-message">{validationErrors.state}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>District *</label>
                          <select
                            name="city"
                            value={formData.city}
                            onChange={handleFormChange}
                            required
                            disabled={loading || !formData.state}
                            className={validationErrors.city ? 'error' : ''}
                          >
                            <option value="">
                              {formData.state ? 'Select district' : 'First select a state'}
                            </option>
                            {availableDistricts.map(district => (
                              <option key={district} value={district}>{district}</option>
                            ))}
                          </select>
                          {validationErrors.city && (
                            <span className="error-message">{validationErrors.city}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Postal Code *</label>
                          <input
                            type="text"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={handlePostalCodeInput}
                            required
                            placeholder="Enter 6-digit postal code"
                            disabled={loading}
                            maxLength="6"
                            className={validationErrors.postalCode ? 'error' : ''}
                          />
                          {validationErrors.postalCode && (
                            <span className="error-message">{validationErrors.postalCode}</span>
                          )}
                        </div>

                        <div className="form-group">
                          <label>Birth Date *</label>
                          <input
                            type="text"
                            name="birthDate"
                            value={formData.birthDate}
                            onChange={(e) => {
                              let value = e.target.value.replace(/[^0-9/]/g, '');

                              // Auto-add slashes after DD and MM
                              if (value.length === 2 && !value.includes('/')) {
                                value += '/';
                              } else if (value.length === 5 && value.split('/').length === 2) {
                                value += '/';
                              }

                              // Limit to DD/MM/YYYY format (10 characters)
                              if (value.length <= 10) {
                                setFormData(prev => ({ ...prev, birthDate: value }));
                              }
                            }}
                            placeholder="DD/MM/YYYY"
                            pattern="\d{2}/\d{2}/\d{4}"
                            title="Date format: DD/MM/YYYY (e.g., 06/10/2003)"
                            required
                            maxLength="10"
                            disabled={loading}
                            className={validationErrors.birthDate ? 'error' : ''}
                          />
                          {validationErrors.birthDate && (
                            <span className="error-message">{validationErrors.birthDate}</span>
                          )}
                        </div>



                        <div className="form-group full-width">
                          <label>Payment Method</label>
                          <select
                            name="paymentMethod"
                            value={formData.paymentMethod}
                            onChange={handleFormChange}
                            disabled={loading}
                          >
                            {getCartTotal() <= walletBalance && walletBalance > 0 && (
                              <option value="Wallet">💰 Wallet (₹{walletBalance.toLocaleString()} available)</option>
                            )}
                            <option value="Online">💳 Online Payment (Razorpay)</option>
                            {/* <option value="Cash">💵 Cash Payment</option> */}
                          </select>
                          {getCartTotal() > walletBalance && walletBalance > 0 && (
                            <div className="wallet-insufficient-notice">
                              <span className="notice-icon">ℹ️</span>
                              <span className="notice-text">
                                Wallet balance (₹{walletBalance.toLocaleString()}) is insufficient for this order (₹{getCartTotal().toLocaleString()})
                              </span>
                            </div>
                          )}

                        </div>
                      </div>

                      <div className="form-actions">
                        <button type="button" className="cancel-button" onClick={() => setShowCartModal(false)} disabled={loading}>
                          Continue Shopping
                        </button>
                        <button type="button" className="cancel-button" onClick={handleSaveFollowUp} disabled={loading || cart.length === 0}>
                          Save for Follow-up
                        </button>
                        <button type="submit" className="submit-button" disabled={loading || cart.length === 0}>
                          Place Order & Generate Invoice
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        )
      }

      {/* Interest Modal */}
      {/* {showInterestModal && (
        <div className="modal-overlay">
          <div className="modal-container interest-modal">
            <div className="modal-header">
              <h2>💡 Save Your Interest</h2>
              <button 
                className="close-button" 
                onClick={() => setShowInterestModal(false)} 
                disabled={loading}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-content">
              <div className="interest-intro">
                <p>📋 Products you're interested in:</p>
                <div className="interest-products-grid">
                  {cart.map((item, index) => (
                    <div key={index} className="interest-product-card">
                      <div className="interest-product-image">
                        {item.images && item.images.length > 0 ? (
                          <img src={item.images[0]} alt={item.name} />
                        ) : (
                          <div className="no-image">📦</div>
                        )}
                      </div>
                      <div className="interest-product-info">
                        <h4 className="interest-product-name">{item.name}</h4>
                        <p className="interest-product-price">₹{item.price.toLocaleString()}</p>
                        <p className="interest-product-quantity">Quantity: {item.quantity}</p>
                        <p className="interest-product-total">Total: ₹{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="interest-summary">
                  <p className="total-interest">💰 Total Interest Value: ₹{cart.reduce((total, item) => total + (item.price * item.quantity), 0).toLocaleString()}</p>
                  <p className="interest-note">We'll contact you with the best offers and availability updates!</p>
                </div>
              </div>

              <form onSubmit={handleInterestSubmit} className="interest-form">
                <div className="form-group">
                  <label>Name *</label>
                  <input
                    type="text"
                    value={interestFormData.name}
                    onChange={(e) => setInterestFormData({...interestFormData, name: e.target.value})}
                    placeholder="Enter your name"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={interestFormData.phone}
                    onChange={(e) => setInterestFormData({...interestFormData, phone: e.target.value})}
                    placeholder="Enter your phone number"
                    required
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>Email (Optional)</label>
                  <input
                    type="email"
                    value={interestFormData.email}
                    onChange={(e) => setInterestFormData({...interestFormData, email: e.target.value})}
                    placeholder="Enter your email"
                    disabled={loading}
                  />
                </div>

                <div className="form-group">
                  <label>When should we follow up?</label>
                  <select
                    value={interestFormData.followUpDays}
                    onChange={(e) => setInterestFormData({...interestFormData, followUpDays: parseInt(e.target.value)})}
                    disabled={loading}
                  >
                    <option value={1}>Tomorrow (1 day)</option>
                    <option value={2}>In 2 days</option>
                    <option value={3}>In 3 days</option>
                    <option value={7}>Next week (7 days)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Additional Notes (Optional)</label>
                  <textarea
                    value={interestFormData.notes}
                    onChange={(e) => setInterestFormData({...interestFormData, notes: e.target.value})}
                    placeholder="Any specific requirements or questions?"
                    rows="3"
                    disabled={loading}
                  />
                </div>

                <div className="form-actions">
                  <button 
                    type="button" 
                    className="cancel-button" 
                    onClick={() => setShowInterestModal(false)} 
                    disabled={loading}
                  >
                    Skip
                  </button>
                  <button 
                    type="submit" 
                    className="submit-button interest-submit" 
                    disabled={loading}
                  >
                    {loading ? 'Saving...' : '📞 Save My Interest'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )} */}

      {/* Image Modal */}
      {
        showImageModal && selectedProduct && (
          <div className="image-modal-overlay" onClick={() => setShowImageModal(false)}>
            <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="image-modal-close" onClick={() => setShowImageModal(false)} title="Close">
                <FaTimes />
              </button>
              <div className="image-modal-body">
                <div className="image-modal-left">
                  <div className="image-modal-image-wrapper">
                    <img
                      src={selectedProduct.imageUrls?.[0] || selectedProduct.imageUrl || 'https://via.placeholder.com/400x400?text=No+Image'}
                      alt={selectedProduct.name || 'Product Image'}
                      className="image-modal-image"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/400x400?text=Image+Not+Available';
                      }}
                    />
                  </div>
                </div>
                <div className="image-modal-right">
                  <h2 className="image-modal-title">{selectedProduct.name || 'Product Name'}</h2>
                  <p className="image-modal-category">{selectedProduct.category || 'Uncategorized'}</p>

                  <div className="image-modal-info-grid">
                    <div className="image-modal-price">
                      <span className="price-label">MRP:</span>
                      <span className="price-value">₹{(selectedProduct.mrp || 0).toLocaleString('en-IN')}</span>
                    </div>
                    <div className="image-modal-stock">
                      <span className="stock-label">Stock:</span>
                      <span className={`stock-value ${selectedProduct.stock > 0 ? 'in-stock' : 'out-stock'}`}>
                        {selectedProduct.stock > 0 ? `${selectedProduct.stock} units` : 'Out of Stock'}
                      </span>
                    </div>
                  </div>

                  {selectedProduct.description && (
                    <div className="image-modal-description">
                      <h3>Product Description</h3>
                      <p>{selectedProduct.description}</p>
                    </div>
                  )}

                  {!selectedProduct.description && (
                    <div className="image-modal-description no-description">
                      <p>No description available for this product.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      }

      {loading && (
        <div className="loading-overlay-full">
          <div className="loading-bar-wrapper">
            <div className="loading-bar"></div>
            <p className="loading-text">{loadingMessage}</p>
          </div>
        </div>
      )}

      <style jsx>{`
     @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

/* Base Container */
.all-products-container {
  font-family: 'Inter', sans-serif;
  padding: 16px;
  background: #f8fafc;
  min-height: 100vh;
  max-width: 100%;
  overflow-x: hidden;
}


/* Loading Bar Styles */
/* Loading Bar Styles */
.loading-overlay-full {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(5px);
  z-index: 99999;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loading-bar-wrapper {
  width: 90%;
  max-width: 400px;
  text-align: center;
}

.loading-bar-container {
  width: 100%;
  padding: 20px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-bottom: 2px solid #10b981;
  text-align: center;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
}

.loading-bar {
  width: 100%;
  max-width: 500px;
  height: 8px;
  background: #d1fae5;
  border-radius: 4px;
  overflow: hidden;
  margin: 0 auto 12px;
  position: relative;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
}

.loading-bar::after {
  content: '';
  position: absolute;
  width: 40%;
  height: 100%;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  animation: loadingSlide 1.5s ease-in-out infinite;
  box-shadow: 0 0 10px rgba(16, 185, 129, 0.5);
}

@keyframes loadingSlide {
  0% {
    left: -40%;
  }
  50% {
    left: 100%;
  }
  100% {
    left: -40%;
  }
}

.loading-text {
  font-size: 1rem;
  color: #065f46;
  font-weight: 600;
  margin: 0;
  letter-spacing: 0.3px;
}
  /* Hide scrollbars for all elements */
* {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

*::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

/* Specifically for the cart items list */
.cart-items-list {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.cart-items-list::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

/* Hide scrollbar for modal content */
.modal-content {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.modal-content::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

/* Hide scrollbar for main container */
.all-products-container {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

.all-products-container::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

/* Hide scrollbar for body and html */
html, body {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* Internet Explorer 10+ */
}

html::-webkit-scrollbar, body::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}


/* Page Header */
.page-header {
  background: var(--mobile-bg-primary);
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--mobile-text-primary);
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-icon {
  font-size: 1.5rem;
}

.page-subtitle {
  color: var(--mobile-text-secondary);
  font-size: 1rem;
  margin: 0;
}

.product-stats {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.stat-item {
  background: var(--mobile-bg-primary-card);
  padding: 16px;
  border-radius: 12px;
  color: white;
  text-align: center;
  min-width: 100px;
  flex: 1;
}

.stat-item.cart-stat {
  cursor: pointer;
  transition: transform 0.2s ease;
}

.stat-item.cart-stat:hover {
  transform: translateY(-2px);
}

.stat-number {
  color: var(--mobile-text-primary);
  font-size: 1.1rem;
  font-weight: 700;
  display: block;
}

.stat-label {
  font-size: 0.75rem;
  opacity: 0.9;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Search Section */
.search-section {
  margin-bottom: 24px;
}

.search-container {
  position: relative;
  max-width: 100%;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #64748b;
  font-size: 16px;
  z-index: 2;
}

.search-input {
  width: 100%;
  padding: 16px 16px 16px 48px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 16px;
  outline: none;
  background: white;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.search-input:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.clear-search {
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #64748b;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
}

.clear-search:hover {
  color: #1e293b;
  background: #f1f5f9;
}

/* Cart Summary Bar */
.cart-summary-bar {
  background: white;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 2px solid #10b981;
}

.cart-summary-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.cart-items-count {
  font-weight: 600;
  color: #374151;
}

.cart-total {
  font-size: 1.25rem;
  font-weight: 700;
  color: #10b981;
}

.view-cart-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.view-cart-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.cart-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.follow-up-button {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.follow-up-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.4);
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
}

/* Products Grid */
.products-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.no-products {
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 16px;
  color: #64748b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.no-products-icon {
  font-size: 4rem;
  opacity: 0.5;
  display: block;
  margin-bottom: 16px;
}

.no-products p {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
}

/* Product Card */
.product-card {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
}

.product-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.product-image-container {
  position: relative;
  width: 100%;
  height: 200px;
  background: #f1f5f9;
}

.product-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-details {
  padding: 16px;
  flex: 1;
}

.product-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  line-height: 1.3;
}

.product-category {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.product-description {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0 0 12px 0;
}

.product-pricing {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.product-price {
  font-size: 1.25rem;
  font-weight: 700;
  color: #10b981;
}

.stock-info {
  font-size: 0.775rem;
  color: #64748b;
  font-weight: 500;
}

.product-actions {
  padding: 16px;
  border-top: 1px solid #f1f5f9;
}

.add-to-cart-button {
  width: 100%;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.add-to-cart-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.add-to-cart-button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

/* Notification Styles */
.notification-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  animation: slideInRight 0.3s ease-out;
}

@keyframes slideInRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.notification-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  border: 1px solid #e2e8f0;
  overflow: hidden;
  min-width: 320px;
  max-width: 400px;
}

.notification-header {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  gap: 12px;
}

.notification-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
  flex-shrink: 0;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
  line-height: 1.3;
}

.notification-message {
  font-size: 13px;
  color: #64748b;
  line-height: 1.4;
}

.notification-close {
  width: 24px;
  height: 24px;
  border: none;
  background: #f1f5f9;
  border-radius: 6px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
  transition: all 0.2s ease;
}

.notification-close:hover {
  background: #e2e8f0;
  color: #1e293b;
}

/* Enhanced Quantity Controls */
.quantity-controls-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.quantity-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
}

.quantity-btn {
  width: 32px;
  height: 32px;
  border: none;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.quantity-btn.decrease {
  color: #dc2626;
}

.quantity-btn.increase {
  color: #10b981;
}

.quantity-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.quantity-btn.decrease:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fecaca;
}

.quantity-btn.increase:hover:not(:disabled) {
  background: #f0fdf4;
  border-color: #bbf7d0;
}

.quantity-btn:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.quantity-display {
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  min-width: 24px;
  text-align: center;
}

.quantity-info {
  text-align: center;
  font-size: 12px;
  color: #10b981;
  font-weight: 500;
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  // padding: 16px;
  box-sizing: border-box;
}

.modal-container {
  background: white;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
}

.modal-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.close-button {
  width: 32px;
  height: 32px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  color: #64748b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover:not(:disabled) {
  background: #e2e8f0;
  color: #1e293b;
}

.close-button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
}

.modal-content {
  flex: 1;
  overflow-y: auto;
  padding: 20px 10px 96px;
  width : 100%;
  max-width: 100%;
}

/* Empty Cart Styles */
.empty-cart {
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
}

.empty-cart-icon {
  font-size: 4rem;
  opacity: 0.5;
  display: block;
  margin-bottom: 16px;
}

.empty-cart p {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 20px 0;
}

.continue-shopping {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.continue-shopping:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

/* Cart Items Section */
.cart-items-section {
  margin-bottom: 24px;
}

.cart-items-section h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.cart-items-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* max-height: 300px; */ /* Removed to display all products */
  overflow-y: visible;
  padding-right: 8px;
}

.cart-item {
  display: flex;
  align-items: center;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  gap: 16px;
}

.cart-item-image {
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
}

.cart-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cart-item-details {
  flex: 1;
  min-width: 0;
}

.cart-item-details h4 {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cart-item-price {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
}

.cart-item-controls {
  display: flex !important;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  visibility: visible !important;
  opacity: 1 !important;
}

.quantity-controls {
  display: flex !important;
  align-items: center;
  gap: 8px;
  visibility: visible !important;
  opacity: 1 !important;
}

.qty-button {
  width: 32px;
  height: 32px;
  border: 2px solid #e2e8f0;
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.75rem;
}

.qty-button:hover:not(:disabled) {
  border-color: #6366f1;
  color: #6366f1;
}

.qty-button:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.5;
}

.quantity-display {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  min-width: 24px;
  text-align: center;
}

.cart-item-total {
  font-size: 1rem;
  font-weight: 700;
  color: #10b981;
  min-width: 80px;
  text-align: right;
}

.remove-button {
  width: 32px;
  height: 32px;
  border: none;
  background: #fee2e2;
  color: #dc2626;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.remove-button:hover:not(:disabled) {
  background: #fecaca;
}

.remove-button:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

/* Cart Total Section */
.cart-total-section {
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  margin-top: 16px;
}

.cart-total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.cart-total-label {
  font-size: 1.25rem;
  font-weight: 600;
  color: #374151;
}

.cart-total-amount {
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
}

.clear-cart-button {
  background: #fee2e2;
  color: #dc2626;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.clear-cart-button:hover:not(:disabled) {
  background: #fecaca;
}

.clear-cart-button:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

/* Customer Form */
.customer-form h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 24px;
}

.customer-form .form-grid .form-group.full-width {
  grid-column: 1 / -1;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 10px 12px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 16px;
  font-family: inherit;
  transition: all 0.2s ease;
  box-sizing: border-box;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.form-group input:disabled,
.form-group textarea:disabled,
.form-group select:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.7;
}

.form-group input.error,
.form-group textarea.error,
.form-group select.error {
  border-color: #dc2626;
}

.error-message {
  color: #dc2626;
  font-size: 0.75rem;
  font-weight: 500;
}

.form-group textarea {
  resize: vertical;
  min-height: 60px;
}

/* Customer Selection Styles */
.customer-selection {
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 20px;
}

.customer-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.3s ease;
}

.customer-select:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.customer-select optgroup {
  font-weight: 600;
  color: #4a5568;
  background: #f7fafc;
}

.customer-select option {
  padding: 8px;
  font-size: 14px;
}

.customer-selection-hint {
  margin-top: 8px;
  font-size: 13px;
  font-weight: 500;
}

.hint-new {
  color: #667eea;
  display: flex;
  align-items: center;
  gap: 6px;
}

.hint-existing {
  color: #10b981;
  display: flex;
  align-items: center;
  gap: 6px;
}

/* Customer Orders Section */
.customer-orders-section {
  background: #f0fdf4;
  border: 2px solid #bbf7d0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
}

.customer-orders-section h4 {
  margin: 0 0 12px 0;
  color: #166534;
  font-size: 16px;
  font-weight: 700;
}

.orders-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 12px;
}

.order-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid #dcfce7;
  font-size: 13px;
}

.order-info {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.order-date,
.order-amount,
.order-items {
  font-weight: 500;
  color: #374151;
}

.order-status .status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-badge.pending {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.paid {
  background: #d1fae5;
  color: #065f46;
}

.status-badge.completed {
  background: #dbeafe;
  color: #1e40af;
}

.orders-summary {
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #dcfce7;
}

.summary-text {
  font-size: 13px;
  color: #166534;
  font-weight: 600;
  font-style: italic;
}

/* Interest Modal Styles */
.interest-modal {
  max-width: 700px;
  max-height: 90vh;
  overflow-y: auto;
}

.interest-intro {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
}

.interest-intro p {
  margin: 0 0 16px 0;
  color: #0369a1;
  font-weight: 600;
  font-size: 16px;
}

.interest-products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.interest-product-card {
  background: white;
  border: 2px solid #e0f2fe;
  border-radius: 12px;
  padding: 16px;
  transition: all 0.3s ease;
}

.interest-product-card:hover {
  border-color: #0ea5e9;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.15);
}

.interest-product-image {
  width: 100%;
  height: 120px;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
}

.interest-product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.interest-product-image .no-image {
  font-size: 32px;
  color: #94a3b8;
}

.interest-product-info {
  text-align: center;
}

.interest-product-name {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.3;
}

.interest-product-price {
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 700;
  color: #059669;
}

.interest-product-quantity {
  margin: 0 0 4px 0;
  font-size: 14px;
  color: #64748b;
}

.interest-product-total {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #0369a1;
  padding: 8px;
  background: #f0f9ff;
  border-radius: 6px;
}

.interest-summary {
  text-align: center;
  padding: 16px;
  background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
  border-radius: 8px;
  border: 1px solid #bbf7d0;
}

.total-interest {
  margin: 0 0 8px 0;
  font-weight: 700;
  color: #059669;
  font-size: 20px;
}

.interest-note {
  margin: 0;
  font-size: 14px;
  color: #166534;
  font-style: italic;
}

.interest-form .form-group {
  margin-bottom: 16px;
}

.interest-form label {
  display: block;
  margin-bottom: 6px;
  color: #374151;
  font-weight: 500;
  font-size: 14px;
}

.interest-form input,
.interest-form select,
.interest-form textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s ease;
}

.interest-form input:focus,
.interest-form select:focus,
.interest-form textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.interest-submit {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.interest-submit:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

/* Wallet Notice Styles */
.wallet-insufficient-notice,
.wallet-empty-notice {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 0.875rem;
}

.wallet-insufficient-notice {
  background: #fef3c7;
  border: 1px solid #f59e0b;
  color: #92400e;
}

.wallet-empty-notice {
  background: #e0f2fe;
  border: 1px solid #0ea5e9;
  color: #0c4a6e;
}

.notice-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.notice-text {
  font-weight: 500;
  line-height: 1.4;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  border-top: 1px solid #e2e8f0;
  padding-top: 20px;
  flex-wrap: wrap;
}

.cancel-button,
.submit-button {
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-width: 140px;
}

.cancel-button {
  background: #f1f5f9;
  color: #64748b;
}

.cancel-button:hover:not(:disabled) {
  background: #e2e8f0;
  color: #1e293b;
}

.submit-button {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
}

.submit-button:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.4);
}

.cancel-button:disabled,
.submit-button:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* Image Modal Styles */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 20px;
  box-sizing: border-box;
  backdrop-filter: blur(4px);
}

.image-modal-content {
  background: white;
  border-radius: 16px;
  max-width: 850px;
  max-height: 85vh;
  width: 100%;
  overflow: hidden;
  position: relative;
  animation: modalZoomIn 0.3s ease-out;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.35);
}

@keyframes modalZoomIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.image-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  color: #1e293b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.image-modal-close:hover {
  background: white;
  transform: rotate(90deg);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.image-modal-body {
  display: flex;
  flex-direction: row;
  max-height: 85vh;
  overflow: hidden;
}

.image-modal-left {
  flex: 0 0 45%;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  border-right: 1px solid #e2e8f0;
}

.image-modal-image-wrapper {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-modal-image {
  max-width: 100%;
  max-height: 450px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.image-modal-right {
  flex: 1;
  padding: 24px;
  background: white;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.image-modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 10px 0;
  line-height: 1.3;
}

.image-modal-category {
  display: inline-block;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  padding: 5px 14px;
  border-radius: 16px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.image-modal-info-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin: 16px 0;
}

.image-modal-price,
.image-modal-stock {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  padding: 14px;
  border-radius: 10px;
  border: 2px solid #e2e8f0;
  transition: all 0.2s ease;
}

.image-modal-price:hover,
.image-modal-stock:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.08);
}

.image-modal-price .price-label,
.image-modal-stock .stock-label {
  display: block;
  font-size: 0.7rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.image-modal-price .price-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #10b981;
}

.image-modal-stock .stock-value {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 6px;
  text-align: center;
}

.image-modal-stock .stock-value.in-stock {
  color: #10b981;
  background: #d1fae5;
  border: 1px solid #10b981;
}

.image-modal-stock .stock-value.out-stock {
  color: #ef4444;
  background: #fee2e2;
  border: 1px solid #ef4444;
}

.image-modal-description {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 2px solid #e2e8f0;
}

.image-modal-description h3 {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 10px 0;
}

.image-modal-description p {
  font-size: 0.9rem;
  line-height: 1.6;
  color: #475569;
  margin: 0;
  white-space: pre-wrap;
}

.image-modal-description.no-description {
  text-align: center;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  border: 2px dashed #cbd5e1;
  margin-top: 16px;
}

.image-modal-description.no-description p {
  color: #94a3b8;
  font-style: italic;
  font-size: 0.85rem;
}

/* Responsive Image Modal */
@media (max-width: 768px) {
  .image-modal-content {
    max-width: 95vw;
    max-height: 90vh;
  }

  .image-modal-body {
    flex-direction: column;
  }

  .image-modal-left {
    flex: 0 0 auto;
    padding: 20px;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
  }

  .image-modal-image {
    max-height: 280px;
  }

  .image-modal-right {
    padding: 20px;
  }

  .image-modal-title {
    font-size: 1.25rem;
  }

  .image-modal-category {
    font-size: 0.75rem;
    padding: 4px 12px;
  }

  .image-modal-info-grid {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }

  .image-modal-price,
  .image-modal-stock {
    padding: 12px;
  }

  .image-modal-price .price-value {
    font-size: 1.35rem;
  }

  .image-modal-stock .stock-value {
    font-size: 0.9rem;
  }

  .image-modal-description h3 {
    font-size: 0.95rem;
  }

  .image-modal-description p {
    font-size: 0.85rem;
  }
}

@media (max-width: 480px) {
  .image-modal-overlay {
    padding: 8px;
  }

  .image-modal-content {
    max-height: 92vh;
  }

  .image-modal-close {
    width: 34px;
    height: 34px;
    top: 8px;
    right: 8px;
    font-size: 16px;
  }

  .image-modal-left {
    padding: 16px;
  }

  .image-modal-image {
    max-height: 220px;
  }

  .image-modal-right {
    padding: 16px;
  }

  .image-modal-title {
    font-size: 1.1rem;
  }

  .image-modal-category {
    font-size: 0.7rem;
    padding: 4px 10px;
  }

  .image-modal-info-grid {
    gap: 8px;
    grid-template-columns: 1fr 1fr;
  }

  .image-modal-price,
  .image-modal-stock {
    padding: 10px;
  }

  .image-modal-price .price-label,
  .image-modal-stock .stock-label {
    font-size: 0.65rem;
    margin-bottom: 4px;
  }

  .image-modal-price .price-value {
    font-size: 1.2rem;
  }

  .image-modal-stock .stock-value {
    font-size: 0.85rem;
    padding: 4px 8px;
  }

  .image-modal-description {
    margin-top: 12px;
    padding-top: 12px;
  }

  .image-modal-description h3 {
    font-size: 0.9rem;
    margin-bottom: 8px;
  }

  .image-modal-description p {
    font-size: 0.8rem;
    line-height: 1.5;
  }

  .image-modal-description.no-description {
    padding: 12px;
  }
}

/* Tablet Styles (768px and up) */
@media (min-width: 768px) {
  .all-products-container {
    padding: 24px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .page-header {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .product-stats {
    flex-wrap: nowrap;
  }

  .image-previews {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .order-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .order-item {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  .order-status {
    align-self: flex-end;
  }

  .cart-item-image {
    width: 80px;
    height: 80px;
  }

  .form-grid {
    grid-template-columns: 1fr 1fr;
  }

  .form-actions {
    justify-content: space-between;
  }

  .cancel-button,
  .submit-button {
    flex: none;
  }
}

/* Desktop Styles (1024px and up) */
@media (min-width: 1024px) {
  .all-products-container {
    padding: 32px;
  }

  .products-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 24px;
  }

  .product-card {
    height: 100%;
  }

  .modal-container {
    max-width: 900px;
  }

  .cart-items-list {
    /* max-height: 400px; */ /* Removed to display all items without scrolling */
  }

  .cart-item-details h4 {
    white-space: normal;
    overflow: visible;
    text-overflow: unset;
  }
}

/* Large Desktop (1440px and up) */
@media (min-width: 1440px) {
  .products-grid {
    grid-template-columns: repeat(4, 1fr);
  }
}

/* Custom scrollbar for cart items list */
.cart-items-list::-webkit-scrollbar {
  width: 6px;
}

.cart-items-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.cart-items-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.cart-items-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Animation for cart items */
.cart-item {
  animation: slideInCart 0.3s ease-out;
}

@keyframes slideInCart {
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

/* Mobile Responsive - Small screens (300px to 767px) */
@media (min-width: 300px) and (max-width: 767px) {
  .all-products-container {
    padding: 8px;
    min-width: 300px;
    max-width: 100vw;
    overflow-x: hidden;
  }

  .page-header {
    padding: 12px;
  }

  .page-title {
    font-size: 1.3rem;
  }

  .stat-item {
    padding: 10px;
    min-width: 70px;
  }

  .stat-number {
    color: var(--mobile-text-primary);
    font-size: 1.3rem;
  }

  /* Product grid - 2 columns for mobile */
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
    width: 100%;
  }

  /* Product card adjustments for mobile */
  .product-card {
    min-width: 0;
    max-width: 100%;
  }

  .product-image-container {
    height: 170px;
  }

  .product-image {
    height: 100% ;
    object-fit: cover;
  }

  .product-info {
    padding: 10px;
  }

  .product-name {
    font-size: 0.85rem;
    line-height: 1.3;
    max-height: 2.6em;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }

  .product-price {
    font-size: 1rem;
    margin: 6px 0;
  }

  .product-stock {
    font-size: 0.75rem;
    padding: 3px 6px;
  }

  .product-actions {
    padding: 10px;
    gap: 6px;
  }

  .add-to-cart-button {
    padding: 8px;
    font-size: 0.8rem;
    min-height: 36px;
  }

  .quantity-button {
    width: 28px;
    height: 28px;
    font-size: 0.9rem;
  }

  .quantity-display {
    width: 28px;
    font-size: 0.9rem;
  }
}

/* Very small screens (300px to 380px) */
@media (min-width: 300px) and (max-width: 380px) {
  .all-products-container {
    padding: 6px;
    min-width: 300px;
  }

  .page-header {
    padding: 10px;
  }

  .page-title {
    font-size: 1.2rem;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 6px;
  }
    .product-image-container
  .product-card {
    min-width: 0;
    max-width: 100%;
    border-radius: 12px;
  }

  .product-image-container {
    height: 153px;
    width: 100%;
    overflow: hidden;
  }

  .product-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .product-info {
    padding: 8px;
  }

  .product-name {
    font-size: 0.7rem;
    font-weight: 500;
    line-height: 1.2;
    max-height: 2.4em;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    word-break: break-word;
  }

  .product-category {
    font-size: 0.6rem;
    margin: 0 0 4px 0;
    letter-spacing: 0.3px;
  }

  .product-description {
    font-size: 0.6rem;
    margin: 0 0 4px 0;
  }

  .product-price {
    font-size: 0.85rem;
    margin: 4px 0;
    font-weight: 600;
  }

  .product-stock {
    font-size: 0.65rem;
    padding: 2px 4px;
    top: 6px;
    right: 6px;
  }

  .product-actions {
    padding: 6px;
    gap: 4px;
  }

  .add-to-cart-button {
    padding: 6px 4px;
    font-size: 0.65rem;
    min-height: 30px;
    gap: 3px;
  }

  .quantity-button {
    width: 24px;
    height: 24px;
    font-size: 0.8rem;
  }

  .quantity-display {
    width: 24px;
    font-size: 0.85rem;
  }

  .stat-item {
    padding: 8px;
    min-width: 60px;
  }

  .stat-number {
    font-size: 1.1rem;
  }

  .stat-label {
    font-size: 0.7rem;
  }

  .search-container input {
    font-size: 0.85rem;
    padding: 10px 12px 10px 36px;
  }
}

/* Extra small screens (300px to 480px) */
@media (max-width: 480px) {
  .all-products-container {
    padding: 8px;
  }

  .products-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }

  .cart-summary-content {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .cart-actions {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .follow-up-button,
  .view-cart-button {
    width: 100%;
    justify-content: center;
  }

  .interest-products-grid {
    grid-template-columns: 1fr;
  }

  .interest-modal {
    max-width: 95vw;
    margin: 10px;
  }

  .cart-item {
    flex-direction: row;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  .cart-item-image {
    width: 50px;
    height: 50px;
  }

  .cart-item-details {
    flex: 1;
    min-width: 120px;
  }

  .cart-item-controls {
    justify-content: center;
    flex-wrap: nowrap;
    width: 100%;
    gap: 8px;
  }

  .quantity-controls {
    flex: 1;
  }

  .cart-item-total {
    min-width: 70px;
  }

  .form-actions {
    flex-direction: column;
  }

  .cancel-button,
  .submit-button {
    width: 100%;
  }

  .modal-container {
    margin: 8px;
    max-height: 95vh;
  }

  .form-actions {
    position: static;
    bottom: auto;
    background: transparent;
    padding-bottom: 0;
    margin-bottom: env(safe-area-inset-bottom, 0px);
  }
}
      `}</style>
    </div >
  );
};

export default AllProducts;